import { ApiError, get, post, type Me } from '../api.js';
import { ago, button, chip, clear, el, empty, input, panel, row, skeleton, toast } from '../ui.js';

/**
 * The dialling floor.
 *
 * The screen answers one question, continuously: who may I lawfully call right
 * now? Not "who is in my book" — a book is a list, and a list cannot tell an
 * agent that the person third from the top lives in California where it is
 * still ten past six in the morning.
 *
 * Three groups, and the distinction between the last two is the whole point:
 *
 *   Callable   every check passed, in that lead's own time zone. Work these.
 *   Waiting    the only objection is the hour where they live. These free
 *              themselves, and the page says when.
 *   Blocked    something else — no consent, a do-not-call listing, an overdue
 *              scrub, the weekly cap, an agency hold. Waiting will not fix any
 *              of it, so it is shown apart from the ones that will.
 *
 * The page refreshes itself, because the answer changes without anybody doing
 * anything: at 8am Pacific a few hundred leads become legal to call, and an
 * agent should not have to know that to benefit from it.
 */

/**
 * A call this platform placed, as the server sees it.
 *
 * The state is the SERVER's, refreshed from `GET /v1/dial/:id` — never a guess
 * the browser keeps. The carrier moves a call through ringing, answering,
 * voicemail and hangup without anybody pressing anything here, and a screen
 * that displayed its own last idea of the state would tell an agent the call
 * was still ringing after the person had already hung up.
 */
interface LiveCall {
  sessionId: string;
  state: string;
  endedAt: string | null;
  transferToUserId: string | null;
  /** The wording that must be read, and the version recorded when it is. */
  disclosure: { version: string; text: string; required: boolean; timing: string } | null;
}

/** A licensed colleague who could take this call. */
interface TransferTarget {
  id: string;
  name: string;
  available: boolean;
  lastSeenAt: string | null;
  isOwner: boolean;
  /** False when they have no telephone number on their record — a transfer rings them. */
  reachable: boolean;
}

/** A promise this agent made, as the server returns it. */
interface PromiseRow {
  id: string;
  leadId: string;
  kind: 'callback' | 'appointment';
  dueAt: string;
  theirPlan: string | null;
  minutesAway: number;
  lead: { id: string; name: string; phone: string | null; state: string | null } | null;
}

interface QueueLead {
  id: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  state: string | null;
  score: number;
  status: string;
  attempts: number;
  localHour: number | null;
  ownerName: string | null;
  /** Minutes of lawful calling left today for this person. */
  minutesUntilClose: number;
  /** Why this one is where it is, in words. Null when nothing moved it. */
  why: string | null;
  /**
   * What each part of the ranking contributed.
   *
   * `callback` is kept apart from the rest because it is a different kind of
   * fact: score, urgency and hour are estimates of how promising somebody is,
   * and this one is a promise that was made to a person.
   */
  /**
   * Optional on purpose, though the server always sends it.
   *
   * The browser and the API are two programs that deploy separately, so "the
   * type says it is there" is a statement about this repository and not about
   * what arrives over the wire. It was NOT optional, `/v1/dial/next` did not
   * send it, and reading `.callback` off undefined threw inside `paint()` —
   * which took out the entire calling floor, from one chip nobody would miss.
   */
  rankParts?: { score: number; urgency: number; hour: number; callback: number };
  /*
   * WHAT THE FILE ALREADY SAYS ABOUT THIS HOUSEHOLD.
   *
   * The API has always returned these — a queue entry is a whole lead — and
   * this screen declared eleven fields and dropped the rest on the floor. So
   * an agent reached "yes, what would that cost me?" and then asked a woman
   * her date of birth, whether she smokes, and how much cover she wanted: all
   * three on the row the vendor sold and the importer kept.
   *
   * That is not a small rudeness. It is half a minute of somebody hearing
   * their agent hunt for things they already told this company, at the exact
   * moment they are deciding whether it is competent.
   *
   * Found by a probe that walked one call from the name coming up to the
   * application being opened, and that reads the front-end source rather than
   * the response — because the response has everything, and the agent does
   * not read the response.
   */
  dob: string | null;
  tobacco: boolean | null;
  requestedFaceCents: number | null;
  coverageInterest: string | null;
  existingCoverageCents: number | null;
  dependents: number | null;
  incomeCents: number | null;
  notes: string | null;
  lastDisposition: string | null;
}

interface Queue {
  callable: QueueLead[];
  counts: { callable: number; waiting: number; blocked: number; scanned: number };
  nextOpensInMinutes: number | null;
  capped: boolean;
  /** Callable leads whose state stops permitting calls within the hour. */
  closingSoon: number;
  /** Callbacks that have come due, sitting at the front of the list. */
  dueCallbacks: number;
  /** People held out of the queue because they asked for a later hour. */
  promised: number;
  /** People rung recently enough that trying again now would test nothing. */
  resting: number;
  /** What is actually stopping the blocked ones, biggest cause first. */
  blockedBy: Array<{ code: string; check: string; message: string; leads: number }>;
}

/** One bucket of the agency's own answer-rate history. */
interface Bucket {
  key: number | string;
  attempts: number;
  contacts: number;
  /** Null below the sample size at which a rate means anything. */
  rate: number | null;
  thin: boolean;
}

interface Reach {
  windowDays: number;
  attempts: number;
  contacts: number;
  baselineRate: number | null;
  thin: boolean;
  capped: boolean;
  byHour: Bucket[];
  byDay: Bucket[];
  byAreaCode: Array<Bucket & { state: string | null }>;
  byState: Bucket[];
  untriedHours: number[];
  /** Recorded calls whose local hour was outside what that state permits. */
  outsideWindow: number;
  bestHour: number | null;
  worstHour: number | null;
  upliftContacts: number | null;
}

/** "10am", "1pm", "12pm" — an hour a person reads without translating it. */
function clockHour(h: number): string {
  if (h === 0) return '12am';
  if (h === 12) return '12pm';
  return h < 12 ? `${h}am` : `${h - 12}pm`;
}

const DAY_NAME = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const pct = (r: number): string => `${Math.round(r * 100)}%`;

/** "38 min", "2h 10m", "tomorrow" — a countdown a person reads at a glance. */
function untilText(minutes: number): string {
  if (minutes <= 0) return 'now';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h < 12) return m ? `${h}h ${m}m` : `${h}h`;
  return 'tomorrow';
}

const fullName = (l: QueueLead): string =>
  [l.firstName, l.lastName].filter(Boolean).join(' ') || 'Unnamed lead';

/**
 * Age last birthday, from a date of birth the API sent as a string.
 *
 * Returned so the card can say "68" instead of "1958-06-13". An agent reading
 * a date of birth off a screen is doing arithmetic out loud while somebody
 * waits, and getting it wrong by one is getting the premium wrong by one band.
 *
 * The QUOTE does not use this. It sends the date of birth and lets the server
 * work the age out on the same clock the rate card is read against — two
 * places computing an age from two clocks is how a quote and its saved copy
 * come to disagree by a year.
 */
function ageFrom(dob: string | null): number | null {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age >= 0 && age < 130 ? age : null;
}

const dollars = (cents: number | null): string =>
  cents === null ? '—' : `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

/** (305) 249-0142 — ten digits, the way a person reads a number back. */
function prettyPhone(raw: string | null): string {
  if (!raw) return '—';
  const d = raw.replace(/\D/g, '');
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/**
 * WHAT YOU SAID YOU WOULD DO.
 *
 * WHY THIS IS THE FIRST THING ON THE PAGE
 * A promise is the warmest thing in a book. They answered, they listened, they
 * did not say no, and they named a time — and the platform's own research pass
 * could not find a single product in this category that surfaces "here are the
 * commitments you made and which ones you kept". What happens instead is
 * universal and expensive: the callback is missed, the lead drops back into the
 * general pool, somebody rings them cold three weeks later with the wrong
 * opening, and a person who was ready to buy is now annoyed. Nothing anywhere
 * records that a sale was lost.
 *
 * WHY IT IS THEIRS AND NOT THE FLOOR'S
 * A list of everybody's commitments is a supervisor's report. An agent looking
 * at forty rows of which six are theirs reads none of them. This is the six.
 *
 * It hides itself when there is nothing owed, because a panel that is always
 * there saying "0" trains people to stop looking at the top of the page.
 */
function promisesDue(host: HTMLElement, go: (p: string) => void): void {
  let rows: PromiseRow[] = [];

  const paint = (): void => {
    clear(host);
    if (!rows.length) { host.hidden = true; return; }
    host.hidden = false;

    const overdue = rows.filter((r) => r.minutesAway < 0);
    const soon = rows.filter((r) => r.minutesAway >= 0);

    const line = (r: PromiseRow): HTMLElement => {
      const late = r.minutesAway < 0;
      const when = late
        ? `${Math.abs(r.minutesAway)} min late`
        : r.minutesAway < 60
          ? `in ${r.minutesAway} min`
          : new Date(r.dueAt).toLocaleString(undefined, {
            weekday: 'short', hour: 'numeric', minute: '2-digit',
          });
      return row({
        title: r.lead?.name ?? 'Unnamed',
        // What they said they would be doing beforehand, shown back to the
        // agent — it is the opening line of the call they are about to make.
        sub: [r.theirPlan ? `"${r.theirPlan}"` : null, prettyPhone(r.lead?.phone ?? null)]
          .filter(Boolean).join(' · '),
        right: [
          chip(when, late ? 'crit' : 'ok'),
          /*
           * Settling it is two buttons and no dialog. A promise that can only
           * be closed by finding the lead, opening it and hunting for a control
           * is a promise that stays open for ever — and then "kept" measures
           * who tidies up rather than who rang.
           */
          button('Done', 'sm pri', () => void settle(r.id, 'kept')),
          button('Drop', 'sm', () => void settle(r.id, 'cancel')),
        ],
        onclick: () => { if (r.lead) go(`/leads/${r.lead.id}`); },
      });
    };

    host.append(panel(
      overdue.length
        ? `${overdue.length} ${overdue.length === 1 ? 'promise is' : 'promises are'} overdue`
        : 'You promised to call these people',
      el('div', null,
        ...overdue.map(line),
        ...soon.map(line)),
    ));
  };

  const load = async (): Promise<void> => {
    try {
      // The next eight hours. A promise for next Tuesday is real and is not
      // what somebody needs at the top of the screen this morning.
      const r = await get<{ items: PromiseRow[] }>('/v1/promises/mine?withinHours=8');
      rows = r.items ?? [];
    } catch { rows = []; }
    paint();
  };

  /** Marks it kept or drops it, then reloads so the row leaves the screen. */
  async function settle(promiseId: string, what: 'kept' | 'cancel'): Promise<void> {
    try {
      await post(`/v1/promises/${promiseId}/${what}`, {});
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'That did not save.', 'crit');
      return;
    }
    await load();
  }

  void load();
  const timer = setInterval(() => { void load(); }, 120_000);
  const observer = new MutationObserver(() => {
    if (!host.isConnected) { clearInterval(timer); observer.disconnect(); }
  });
  queueMicrotask(() => {
    if (host.parentElement) observer.observe(host.parentElement, { childList: true, subtree: true });
  });
}

/**
 * WHETHER THE FLOOR KEEPS ITS WORD — and whether asking for a plan helps.
 *
 * Two questions on one panel because they are the same question asked twice.
 * The first is the honest measure of a calling floor that nothing else reports:
 * of the commitments made this month, how many were actually kept. The second
 * is this agency running a real experiment on its own book.
 *
 * WHAT THE SECOND COLUMN ACTUALLY IS
 * `withPlan` versus `withoutPlan` splits kept-rate by whether the agent got the
 * person to say what they would be doing beforehand. That effect was measured
 * at N = 287,228 in a voting experiment and at N = 3,272 in a vaccination one —
 * +4.1pp and +4.0pp respectively — and has never, as far as the published
 * literature goes, been measured in phone sales. If it holds here it is a script
 * change worth making, and the number is the agency's own rather than somebody's
 * white paper.
 *
 * Only for whoever manages the floor. An agent seeing colleagues ranked by
 * kept-rate is a leaderboard, and this is meant to start a conversation.
 */
function keptRates(host: HTMLElement, me: Me): void {
  if (!me.capabilities.includes('user.manage')) { host.hidden = true; return; }

  const pct = (v: number | null): string => (v === null ? '—' : `${Math.round(v * 100)}%`);

  const load = async (): Promise<void> => {
    let rows: Array<{
      ownerId: string; settled: number; kept: number; keptRate: number | null; thin: boolean;
      withPlan: { settled: number; keptRate: number | null };
      withoutPlan: { settled: number; keptRate: number | null };
    }> = [];
    let people = new Map<string, string>();
    try {
      const r = await get<{ items: typeof rows }>('/v1/promises/kept');
      rows = r.items ?? [];
      const team = await get<{ items: Array<{ id: string; firstName: string; lastName: string }> }>('/v1/users');
      people = new Map((team.items ?? []).map((u) => [u.id, `${u.firstName} ${u.lastName}`.trim()]));
    } catch { rows = []; }

    clear(host);
    if (!rows.length) { host.hidden = true; return; }
    host.hidden = false;

    const withPlan = rows.reduce((n, r) => n + r.withPlan.settled, 0);
    const withoutPlan = rows.reduce((n, r) => n + r.withoutPlan.settled, 0);
    const keptWithPlan = rows.reduce((n, r) => n + Math.round((r.withPlan.keptRate ?? 0) * r.withPlan.settled), 0);
    const keptWithout = rows.reduce((n, r) => n + Math.round((r.withoutPlan.keptRate ?? 0) * r.withoutPlan.settled), 0);

    host.append(panel('Promises kept', el('div', null,
      ...rows.map((r) => row({
        title: people.get(r.ownerId) ?? r.ownerId,
        sub: r.thin
          ? `${r.settled} settled — too few to state a rate`
          : `${r.kept} of ${r.settled} kept`,
        right: chip(pct(r.keptRate), r.keptRate === null ? 'info' : r.keptRate >= 0.7 ? 'ok' : 'warn'),
      })),
      /*
       * The floor-wide split, stated only when both sides have enough to
       * compare. Quoting a difference between 4 promises and 3 is how a script
       * change gets made on noise.
       */
      withPlan >= 20 && withoutPlan >= 20
        ? el('div', { class: 'note', style: 'margin-top:12px' },
          el('b', null, 'Does asking what they will be doing first help? '),
          `Promises where the person said their plan: ${pct(keptWithPlan / withPlan)} kept `
          + `(${withPlan}). Where they did not: ${pct(keptWithout / withoutPlan)} kept `
          + `(${withoutPlan}).`)
        : el('div', {
          style: 'margin-top:12px;font-size:12px;color:var(--ink-4)',
          text: 'Not enough settled promises yet to say whether asking for their plan helps. '
            + 'It needs 20 on each side.',
        }))));
  };

  void load();
}

/**
 * "I am at my desk and can take a transfer."
 *
 * WHY IT IS A PROMISE AND NOT AN INFERENCE
 * A warm transfer puts a stranger on a line to whoever this says is there. If
 * the platform guessed — a recent request, an open tab — it would eventually
 * guess wrong, and the cost of guessing wrong is a person on hold listening to
 * nobody. So it is a flag somebody sets deliberately, and the moment they set
 * it is stamped.
 *
 * WHY IT HAS A HEARTBEAT
 * A promise with no freshness beside it keeps promising after the laptop has
 * closed. The server stops believing `available` after five minutes without a
 * sign of life, so this says "still here" every two — which also means an agent
 * who shuts the lid at five o’clock stops being offered transfers at five past,
 * without having to remember to switch anything off.
 */
function atMyDesk(host: HTMLElement, me: Me): void {
  let on = false;
  let busy = false;
  let beat: ReturnType<typeof setInterval> | null = null;

  const paint = (): void => {
    clear(host);
    host.append(el('div', {
      style: 'display:flex;gap:10px;align-items:center;justify-content:flex-end;margin:-6px 0 10px',
    },
    el('span', {
      style: 'font-size:13px;color:var(--ink-3)',
      text: on
        ? 'Colleagues can transfer live calls to you.'
        : 'You will not be offered transferred calls.',
    }),
    button(
      busy ? 'One moment…' : on ? 'At my desk' : 'Not at my desk',
      on ? 'sm pri' : 'sm',
      () => void set(!on),
    )));
  };

  async function set(next: boolean): Promise<void> {
    if (busy) return;
    busy = true;
    paint();
    try {
      await post('/v1/me/availability', { available: next });
      on = next;
      if (beat) { clearInterval(beat); beat = null; }
      if (on) beat = setInterval(() => { void post('/v1/me/heartbeat', {}).catch(() => {}); }, 120_000);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'That did not save.', 'crit');
    } finally {
      busy = false;
      paint();
    }
  }

  // Stop the heartbeat when the screen goes away, or it outlives the page.
  const observer = new MutationObserver(() => {
    if (!host.isConnected) {
      if (beat) { clearInterval(beat); beat = null; }
      observer.disconnect();
    }
  });
  queueMicrotask(() => {
    if (host.parentElement) observer.observe(host.parentElement, { childList: true, subtree: true });
  });

  void me;
  paint();
}

export function dialingView(me: Me, go: (p: string) => void): HTMLElement {
  const host = el('div');
  const canHold = me.capabilities.includes('org.manage') || me.user.role === 'manager';
  const canResume = me.capabilities.includes('org.manage');

  host.append(el('div', { class: 'vhead' },
    el('span', { class: 'kicker', text: 'Dialling' }),
    el('h1', { text: 'Who you can call right now' })));

  const desk = el('div');
  host.append(desk);
  atMyDesk(desk, me);

  /*
   * Above the queue, because a promise outranks any cold name on it. The queue
   * answers "who could I ring"; this answers "who am I already expected to".
   */
  const promised = el('div');
  host.append(promised);
  promisesDue(promised, go);

  /* Only rendered for whoever manages the floor — it checks inside. */
  const kept = el('div');
  host.append(kept);
  keptRates(kept, me);

  const summary = el('div');
  const body = el('div');
  const holdBar = el('div');
  // Above the queue, because it is work already done that is not yet counted.
  // A call read and never confirmed is a sale sitting in a drawer.
  const waiting = el('div');
  /*
   * And above the cold queue for the same reason, one step further back: a
   * client who already bought, whose draft is about to bounce, is worth more
   * than any name on a list — and calling them is the difference between
   * keeping a commission and being charged back for it.
   */
  const reasons = el('div');
  /*
   * A report rather than a queue, so it sits under the work. It is the answer
   * to "when should we be doing this at all", which is worth knowing and is
   * never the next thing to do.
   */
  const reach = el('div');
  /*
   * The session. Everything else on this screen is a list of what could be
   * done; this is the thing that does it, so it sits at the top and the rest
   * disappears while it is running.
   */
  const session = el('div');
  host.append(holdBar, session, waiting, reasons, summary, body, reach);
  void confirmQueue(waiting, go);
  void reasonsPanel(reasons, go);
  void reachPanel(reach);

  const below = [waiting, reasons, summary, body, reach];
  const showBelow = (on: boolean): void => {
    for (const part of below) part.hidden = !on;
  };
  callSession(session, {
    onStart: () => { showBelow(false); stop(); },
    onStop: () => { showBelow(true); void load(); timer = setInterval(() => { void load(); }, 30_000); },
  });

  let timer: ReturnType<typeof setInterval> | null = null;

  /**
   * Stops the refresh when the page is replaced. Without this the timer keeps
   * firing against a screen nobody is looking at, which is a request every
   * thirty seconds, for as long as the tab stays open, forever.
   */
  const stop = (): void => { if (timer) { clearInterval(timer); timer = null; } };
  const observer = new MutationObserver(() => {
    if (!host.isConnected) { stop(); observer.disconnect(); }
  });
  queueMicrotask(() => {
    if (host.parentElement) observer.observe(host.parentElement, { childList: true });
  });

  async function load(): Promise<void> {
    try {
      const q = await get<Queue>('/v1/leads/queue?limit=100');
      paintHold();
      paintSummary(q);
      paintList(q);
    } catch (e) {
      clear(body).append(el('div', { class: 'warnbox' },
        e instanceof ApiError ? e.message : 'Could not load the queue.'));
    }
  }

  function paintHold(): void {
    clear(holdBar);
    const org = me.org;
    if (org?.dialingPaused) {
      const resume = button('Lift the hold', 'pri', async () => {
        if (!confirm('Start calling again for the whole agency?')) return;
        try {
          await post('/v1/org/dialing', { paused: false });
          toast('Calling has resumed.', 'ok');
          go('/dialing');
        } catch (e) { toast(e instanceof ApiError ? e.message : 'Could not lift the hold.', 'crit'); }
      });
      holdBar.append(el('div', { class: 'warnbox', style: 'margin-bottom:16px' },
        el('b', null, 'All calling is stopped. '),
        org?.dialingPausedBy ? `${org.dialingPausedBy} stopped it` : 'Somebody stopped it',
        org?.dialingPausedReason ? ` — "${org.dialingPausedReason}"` : '',
        '.',
        canResume
          ? el('div', { style: 'margin-top:12px' }, resume)
          : el('div', { style: 'margin-top:8px;font-size:13px' },
            'An agency owner or administrator can lift it.')));
      return;
    }

    if (!canHold) return;
    const stopBtn = button('Stop all calling', 'danger sm', async () => {
      const reason = prompt(
        'Stop all outbound calling for the agency?\n\n'
        + 'Every phone stops immediately. Say why — it goes in the audit trail '
        + 'and tells whoever looks at this next what happened.',
      );
      if (reason === null) return;
      try {
        await post('/v1/org/dialing', { paused: true, reason: reason || undefined });
        toast('All calling stopped.', 'ok');
        go('/dialing');
      } catch (e) { toast(e instanceof ApiError ? e.message : 'Could not stop calling.', 'crit'); }
    });
    holdBar.append(el('div', {
      style: 'display:flex;justify-content:flex-end;margin-bottom:12px',
    }, stopBtn));
  }

  function paintSummary(q: Queue): void {
    clear(summary);
    const { counts } = q;

    const tile = (
      value: string, label: string, note: string | null, accent: string,
    ): HTMLElement => el('div', { class: 'tile', style: `--accent:${accent}` },
      el('div', { class: 'k', text: label }),
      el('div', { class: 'v', text: value }),
      note ? el('div', { class: 'd', text: note }) : null);

    summary.append(el('div', { class: 'tiles' },
      tile(
        String(counts.callable), 'Callable now',
        counts.callable ? 'Every check passed' : 'Nothing is callable this minute',
        'var(--good)',
      ),
      tile(
        String(counts.waiting), 'Waiting on the clock',
        q.nextOpensInMinutes === null
          ? 'None waiting'
          : `Next opens in ${untilText(q.nextOpensInMinutes)}`,
        'var(--copper)',
      ),
      tile(
        String(counts.blocked), 'Blocked',
        counts.blocked ? 'Time will not fix these' : 'Nothing blocked',
        'var(--crit)',
      )));

    /*
     * THE ONE PIECE OF INFORMATION THAT CHANGES WHAT AN AGENT DOES NEXT.
     *
     * A book spanning four time zones does not run out all at once. Michigan
     * shuts two hours before Colorado, every single day, and an agent working
     * strictly best-first arrives at the Michigan names after the door has
     * closed on all of them. Nothing anywhere records that: the calls that were
     * never possible leave no trace, and tomorrow the queue looks fine again.
     *
     * The queue already moves them up. This says so out loud, because an order
     * nobody can see is an order nobody trusts.
     */
    /*
     * Above the closing-window notice, because it beats it.
     *
     * Everything else on this screen is somebody who has not said they want to
     * talk. These people did, and named an hour, and that hour has passed.
     * There is nothing else in a book worth more, and missing them is the way
     * agencies lose sales they had already half made — the callback slips, the
     * lead falls back into the general pool, somebody rings them cold three
     * weeks later with the wrong opening line.
     */
    if (q.dueCallbacks > 0) {
      summary.append(el('div', { class: 'okbox', style: 'margin-top:14px' },
        el('b', null, `${q.dueCallbacks} callback${q.dueCallbacks === 1 ? ' is' : 's are'} due now. `),
        'These are at the very top of the list. They asked you to ring them by this time, which '
        + 'makes them worth more than anything else in front of you.'));
    }

    if (q.closingSoon > 0) {
      summary.append(el('div', { class: 'warnbox', style: 'margin-top:14px' },
        el('b', null, `${q.closingSoon} of these stop being callable within the hour. `),
        'They are near the top of the list. Their state closes before the rest of your book '
        + 'does, so calls not made now are calls that cannot be made today.'));
    }

    if (q.promised > 0) {
      summary.append(el('p', {
        class: 's', style: 'font-size:12.5px;color:var(--ink-3);margin:8px 0 0',
        text: `${q.promised} more asked to be called at a later hour and are being held until then. `
          + 'Ringing somebody early spends the goodwill that made them agree.',
      }));
    }

    /*
     * Said out loud, because otherwise this is indistinguishable from a book
     * that has run out. It has not — those people were rung recently, and
     * ringing them again this hour would test nothing except whether they mind.
     */
    if (q.resting > 0) {
      summary.append(el('p', {
        class: 's', style: 'font-size:12.5px;color:var(--ink-3);margin:8px 0 0',
        text: `${q.resting} were rung recently and are resting. Somebody who did not answer at `
          + 'ten is not somebody to ring at ten past — they come back later today or tomorrow, '
          + 'at a different hour.',
      }));
    }

    if (counts.blocked > 0) {
      /*
       * What is stopping them, with counts, instead of a list of things it
       * might be.
       *
       * This used to read "a consent record, a scrub, or a suppression lifted"
       * — three possibilities and no answer. An agency whose entire book is
       * blocked because no registry has been loaded reads that, goes hunting
       * through three settings screens, and concludes the software is broken.
       * The gate worked out the real answer for every one of those rows before
       * this line was drawn.
       */
      summary.append(el('div', { style: 'margin-top:12px' },
        el('p', {
          class: 's',
          style: 'font-size:12.5px;color:var(--ink-3);margin:0 0 8px',
          text: `${counts.blocked.toLocaleString()} blocked, and waiting will not fix any of it. `
            + 'What is stopping them:',
        }),
        el('div', { class: 'rows' }, ...q.blockedBy.map((b) => row({
          title: `${b.leads.toLocaleString()} — ${b.check}`,
          sub: b.message,
          right: b.code === 'scrub_stale'
            ? button('Compliance', 'sm', () => go('/compliance'))
            : button('The book', 'sm', () => go('/leads')),
        })))));
    }

    if (q.capped) {
      summary.append(el('p', {
        class: 's', style: 'font-size:12.5px;color:var(--ink-3);margin:8px 0 0',
      }, `Counted from the ${counts.scanned} highest-scoring open leads. There are more behind them.`));
    }
  }

  function paintList(q: Queue): void {
    clear(body);

    if (!q.callable.length) {
      const [title, why] = q.counts.waiting > 0 && q.nextOpensInMinutes !== null
        ? [
          `${q.counts.waiting} lead${q.counts.waiting === 1 ? '' : 's'} waiting on the clock`,
          `Nothing is callable this minute. The first opens in ${untilText(q.nextOpensInMinutes)}, `
          + 'and this page will pick them up on its own.',
        ]
        : q.counts.blocked > 0
          ? [
            'Nothing callable',
            'What is left is blocked for reasons waiting will not fix — a missing consent '
            + 'record, an overdue scrub, or a suppression.',
          ]
          : ['No open leads', 'Import a list and they will appear here as their windows open.'];
      body.append(panel(null, empty(title, why)));
      return;
    }

    const rows = el('div', { class: 'qlist' });
    for (const lead of q.callable) {
      const hour = lead.localHour === null ? null : Math.floor(lead.localHour);
      rows.append(el('button', {
        type: 'button',
        class: 'qrow',
        onclick: () => go(`/leads/${lead.id}`),
      },
      el('span', { class: 'qscore', text: String(lead.score) }),
      el('span', { class: 'qmain' },
        el('span', { class: 'qname', text: fullName(lead) }),
        el('span', { class: 'qsub', text: prettyPhone(lead.phone) })),
      el('span', { class: 'qmeta' },
        lead.state ? chip(lead.state) : null,
        // Ahead of the clock chip, because it beats it. Everybody else on this
        // list is a stranger; this person asked to be rung.
        (lead.rankParts?.callback ?? 0) > 0 ? chip('asked for a call back', 'ok') : null,
        // The clock beats the score into the eye, on purpose: this is the only
        // thing on the row that expires.
        lead.minutesUntilClose > 0 && lead.minutesUntilClose <= 60
          ? chip(`${lead.minutesUntilClose} min left`, 'crit')
          : null,
        hour === null
          ? null
          : el('span', {
            class: 'qhour',
            title: 'Local time where this person lives',
            text: `${String(hour).padStart(2, '0')}:00 there`,
          }),
        lead.attempts
          ? el('span', { class: 'qhour', text: `${lead.attempts} attempt${lead.attempts === 1 ? '' : 's'}` })
          : null)));
    }

    body.append(panel(
      `Callable now — ${q.counts.callable}`,
      el('p', {
        class: 's',
        style: 'font-size:12.5px;color:var(--ink-3);margin:0 0 12px',
      }, 'Best first, with anything whose state is about to close moved up. Every one of these '
        + 'passed the compliance gate in its own time zone.'),
      rows,
    ));
  }

  clear(body).append(skeleton(4));
  void load();
  // Half a minute. The answer changes on its own — a window opens somewhere, a
  // colleague works a lead — and an agent should not have to reload to find out.
  timer = setInterval(() => { void load(); }, 30_000);

  return host;
}


/* ------------------------------------------------- calls awaiting a person -- */

interface WaitingReading {
  id: string;
  leadId: string;
  createdAt: string;
  proposed: {
    outcome: { value: string };
    carrier: { value: string } | null;
    premium: { value: { cents: number; mode: string } } | null;
    ambiguities: string[];
  };
}

const OUTCOME_SHORT: Record<string, string> = {
  application_started: 'Agreed to apply',
  interested: 'Interested',
  callback: 'Wants a callback',
  not_interested: 'Not interested',
  do_not_call: 'Asked not to be called',
  unknown: 'Unclear',
};

/**
 * Everything read off a call that nobody has confirmed yet.
 *
 * WHY THIS SCREEN HAD TO EXIST BEFORE THE FEATURE WAS USABLE BY MORE THAN ONE
 * PERSON.
 *
 * A reading appears on its own lead and nowhere else. That is fine for the one
 * agent who just made the call and is still looking at the record. It is
 * useless at any volume: twenty calls in an afternoon means twenty leads an
 * agent has to remember to reopen, and the ones they forget are sales that
 * quietly never reach the book. `GET /v1/call-readings` existed and returned
 * exactly this list, and nothing in the product called it.
 *
 * It sits on the dialling floor rather than behind its own tab, because this is
 * the screen an agent already has open, and because work already done but not
 * yet counted should be in front of them before the next number is.
 */
async function confirmQueue(host: HTMLElement, go: (p: string) => void): Promise<void> {
  clear(host);
  let items: WaitingReading[] = [];
  try {
    items = (await get<{ items: WaitingReading[] }>('/v1/call-readings?limit=25')).items;
  } catch {
    // An extra, not the screen. The dialling queue still loads without it.
    return;
  }
  if (items.length === 0) return;

  const rows = el('div', { class: 'rows' });
  for (const r of items) {
    const p = r.proposed;
    const bits: string[] = [OUTCOME_SHORT[p.outcome.value] ?? p.outcome.value];
    if (p.carrier) bits.push(p.carrier.value);
    if (p.premium) {
      bits.push(`$${(p.premium.value.cents / 100).toFixed(2)} ${p.premium.value.mode}`);
    }
    if (p.ambiguities.length) bits.push('needs a choice');

    const open = button('Open', 'sm');
    open.addEventListener('click', () => go(`/leads/${r.leadId}`));
    rows.append(row({ title: bits.join(' · '), sub: ago(r.createdAt), right: open }));
  }

  host.append(panel(
    items.length === 1 ? 'One call is waiting for you' : `${items.length} calls are waiting for you`,
    el('div', { class: 'note', style: 'margin-bottom:12px' },
      el('b', null, 'Read, but not confirmed by anybody yet. '),
      'Nothing here is in the book of business until you say it is right.'),
    rows,
  ));
}

/* ---------------------------------------------------------- the session -- */

interface NextResult {
  lead: QueueLead | null;
  leasedUntil: string | null;
  remaining: number;
  /** The agency has stopped all outbound calling by hand. */
  stopped: boolean;
  /**
   * Whether this name's campaign permits dialling with no press.
   *
   * Optional on purpose: a deployment running an older API sends nothing, and
   * `?? false` then means hands-free simply does not engage. The failure of a
   * missing field must be "the agent presses Call", never "the platform starts
   * dialling on its own".
   */
  autoDial?: boolean;
}

/**
 * The dispositions an agent reaches for, in the order a call actually goes.
 *
 * WHY THERE ARE NINE AND NOT FIFTEEN
 * The vocabulary has fifteen. An agent hunting through fifteen buttons between
 * calls loses more time than the whole rest of this screen saves, and the six
 * that are missing here are all reachable on the lead record where they belong.
 * The number is the shortcut: hand on the keyboard, eyes on the next name.
 */
const QUICK: Array<{ key: string; code: string; label: string; tone: string }> = [
  { key: '1', code: 'no_answer', label: 'No answer', tone: '' },
  { key: '2', code: 'voicemail', label: 'Voicemail', tone: '' },
  { key: '3', code: 'busy', label: 'Busy', tone: '' },
  { key: '4', code: 'not_interested', label: 'Not interested', tone: '' },
  { key: '5', code: 'callback', label: 'Call back', tone: 'cop' },
  { key: '6', code: 'qualified', label: 'Interested', tone: 'ok' },
  { key: '7', code: 'appointment', label: 'Appointment set', tone: 'ok' },
  { key: '8', code: 'wrong_number', label: 'Wrong number', tone: '' },
  { key: '9', code: 'do_not_contact', label: 'Do not call', tone: 'crit' },
];

/**
 * WHY IT DID NOT CLOSE — the reasons an agent hears often enough to want a chip.
 *
 * WHY THESE EIGHT AND NOT THE WHOLE VOCABULARY
 * The server knows seventeen. Eight fit on one row without wrapping, and a row
 * that wraps is a row an agent scans instead of hitting. The rest are on the
 * lead record, where there is time to think. Ordered roughly by how often they
 * are expected — which is a guess, because nobody has ever measured this, and
 * `otherRate` is what will tell us the guess was wrong.
 *
 * WHY IT IS CAPTURED DURING THE CALL AND NOT AFTER IT
 * The obvious design is to ask after the disposition. That turns one keystroke
 * into two at the exact moment an agent is trying to get to the next name, and
 * a capture that costs a second at the wrong moment fills up with whatever
 * clears the screen fastest. So the row is live while the call is: the agent
 * taps it the moment they HEAR it — mid-sentence, while the person is still
 * explaining — and the disposition afterwards is the same single keystroke it
 * always was.
 */
const WHY_NOT: Array<{ code: string; label: string }> = [
  { code: 'cannot_afford', label: 'Cannot afford' },
  { code: 'money_timing', label: 'After they get paid' },
  { code: 'already_covered', label: 'Already covered' },
  { code: 'needs_spouse', label: 'Needs the spouse' },
  { code: 'health_ineligible', label: 'Health' },
  { code: 'distrusts_phone', label: 'Not over the phone' },
  { code: 'no_recollection', label: 'Never asked for a quote' },
  { code: 'wants_time', label: 'Wants to think' },
];

/** The outcomes that mean a live human was on the line. Matches the server. */
const SPOKE = new Set([
  'answered', 'callback', 'not_interested', 'do_not_contact', 'qualified',
  'unqualified', 'appointment', 'transfer', 'sale', 'wrong_number',
]);

/**
 * One lead at a time, in place, with the next one already loaded.
 *
 * WHY THIS EXISTS WHEN THERE IS ALREADY A QUEUE
 * The queue is a list of links. Working it means: click a name, wait for the
 * lead page, find the number, dial, talk, find the disposition control, save,
 * navigate back, wait for the queue, find your place again. That is six or
 * seven seconds of nothing between every call and two page loads, and across a
 * three-hundred-dial day it is over an hour of an agent's time spent watching
 * screens redraw. It is also why agents print lists and work off paper, which
 * is how a compliance system ends up with no idea what was called.
 *
 * Nothing here navigates. The next lead is claimed and rendered before the
 * current call ends, a disposition is one keystroke, and the screen never
 * reloads.
 *
 * WHY IT CLAIMS ONE AHEAD
 * Instant means already fetched. The cost is that an agent holds two leads
 * rather than one — the one they are on and the one they are about to be on —
 * and on a shared pool that is two names a colleague cannot see. Four minutes,
 * two leads, and it is the difference between a screen that feels immediate and
 * one that stutters at the exact moment an agent is trying to keep momentum.
 */
function callSession(
  host: HTMLElement, hooks: { onStart: () => void; onStop: () => void },
): void {
  let running = false;
  let current: QueueLead | null = null;
  let ahead: QueueLead | null = null;
  let busy = false;
  const skipped: string[] = [];
  const stats = { dials: 0, spoke: 0, startedAt: 0 };
  let keyHandler: ((e: KeyboardEvent) => void) | null = null;
  let holdTimer: ReturnType<typeof setInterval> | null = null;
  let lost = false;
  let stopped = false;
  /**
   * What the gate says is stopping the blocked leads, fetched only when the
   * queue comes up empty.
   *
   * Only then, because it is the one moment an agent needs it and the rest of
   * the time it would be a second request behind every hand-off. An empty queue
   * is rare and a stalled agent is expensive.
   */
  let blockedBy: Queue['blockedBy'] = [];
  /** Whether the pricing panel is open under the card. Reset on every new name. */
  let quoting = false;
  /**
   * The call this platform placed for the lead on screen, when it placed one.
   *
   * Null means nobody has pressed Call — which is the ordinary state, because
   * an agency with no telephony provider configured works exactly as it did
   * before and the agent dials from the number on the card.
   */
  let placing = false;
  let call: LiveCall | null = null;
  /** Polls the carrier's view of the live call. Cleared the moment it ends. */
  let callTimer: ReturnType<typeof setInterval> | null = null;
  /** Who this call could be handed to, fetched once the call is up. */
  let targets: TransferTarget[] | null = null;
  /** Open when the agent is choosing who to transfer to. */
  let choosing = false;
  /**
   * Why this one is not closing, tapped while the call is still live.
   *
   * Cleared with the lead, never carried to the next name — a reason that
   * survived onto somebody else's record would be worse than no reason at all,
   * because it would look like evidence.
   */
  let whyNot: string | null = null;
  /**
   * What they said they would be doing before the callback, in their words.
   *
   * Looks like a curiosity. It is the single best-evidenced thing on this
   * screen. Nickerson & Rogers (Psychological Science 2010, N = 287,228) ran a
   * telephone script that asked when, where, and what you would be doing
   * beforehand: +4.1pp on actual follow-through among people reached, where a
   * standard reminder moved nothing at all. Asking the question is the
   * mechanism; storing the answer is what lets this agency measure the effect
   * on its own book, which nobody in phone sales ever has.
   */
  let theirPlan = '';

  /* ------------------------------------------------------ hands-free ---
   *
   * The platform dials the next name on its own. The agent finishes one call
   * and the next is already ringing; they never press anything to dial.
   *
   * WHAT THIS IS NOT
   * It is not predictive dialing. Exactly one call is placed, for one agent who
   * has just finished the last one, which is the same 1:1 the manual path has.
   * Nothing here can change that, and after 0026_no_predictive.sql nothing in
   * the database can express the alternative. Over-dialing produces abandoned
   * calls; 47 CFR 64.1200(a)(7)(i)(A) then requires a prerecorded message on
   * them, and Michigan bans recorded solicitation outright.
   *
   * THE COUNTDOWN IS NOT A COURTESY
   * Without a pause the agent hears "hello" before they have seen who they are
   * talking to — which throws away the one thing this screen is for. A few
   * seconds is the difference between a hands-free dialer and a dialer that
   * ambushes its own operator, and the bar is cancellable because the moment an
   * agent needs to read a note is the moment the machine decides not to wait.
   *
   * THE FAILSAFE THAT ACTUALLY MATTERS
   * An agent who walks away must not leave a machine dialling households that
   * reach nobody. Every one of those is an abandoned call. So the countdown
   * refuses to fire while the tab is hidden, and it stops entirely after a run
   * of dials the agent never acted on — see `unattended` below.
   */
  const HANDS_FREE_KEY = 'cv.handsfree';
  /** Seconds between the name appearing and the line opening. */
  const COUNTDOWN_SECONDS = 4;
  /**
   * How many auto-dials in a row may end with the agent doing nothing before
   * the screen decides nobody is sitting there.
   *
   * Two, not ten. Every call past the last one an agent actually worked is a
   * household connected to silence, and the cost of being wrong in the cautious
   * direction is that somebody presses a button.
   */
  const UNATTENDED_LIMIT = 2;

  let handsFree = false;
  try { handsFree = localStorage.getItem(HANDS_FREE_KEY) === '1'; } catch { handsFree = false; }
  /** What the server said about THIS lead's campaign. Never assumed true. */
  let mayAutoDial = false;
  /** The same answer for the pre-claimed name, so a skip does not lose it. */
  let aheadAutoDial = false;
  let countdownTimer: ReturnType<typeof setInterval> | null = null;
  let countdownLeft = 0;
  /** Auto-dials since the agent last did anything. Reset by any disposition. */
  let unattended = 0;

  const setHandsFree = (on: boolean): void => {
    handsFree = on;
    try { localStorage.setItem(HANDS_FREE_KEY, on ? '1' : '0'); } catch { /* private mode */ }
    if (!on) cancelCountdown();
    else { unattended = 0; maybeAutoDial(); }
    paint();
  };

  function cancelCountdown(): void {
    if (countdownTimer) { clearInterval(countdownTimer); countdownTimer = null; }
    countdownLeft = 0;
  }

  /**
   * Start the countdown to dialling this name, if everything says we should.
   *
   * Every condition here is a reason NOT to dial, and they are all checked in
   * one place so that adding a new one cannot be forgotten at a second call
   * site. The default in every unclear case is to do nothing and let the agent
   * press Call.
   */
  function maybeAutoDial(): void {
    cancelCountdown();
    if (!handsFree || !mayAutoDial) return;
    if (!running || busy || stopped || lost || quoting) return;
    if (!current || call || placing) return;
    // Nothing to dial, or the gate has already refused this name on the list.
    if (!current.phone) return;
    if (unattended >= UNATTENDED_LIMIT) return;
    // A backgrounded tab is an agent who is not looking at this screen.
    if (typeof document !== 'undefined' && document.hidden) return;

    countdownLeft = COUNTDOWN_SECONDS;
    paint();
    countdownTimer = setInterval(() => {
      countdownLeft -= 1;
      if (countdownLeft > 0) { paint(); return; }
      cancelCountdown();
      // Re-checked at the moment of firing, not only when the timer was set:
      // four seconds is long enough for the agent to start a quote, for the
      // agency to throw the kill switch, or for the lead to be taken.
      if (!handsFree || !running || busy || stopped || lost || quoting || call || placing || !current) {
        paint();
        return;
      }
      unattended += 1;
      void placeCall();
    }, 1000);
  }

  /** An agent who switches tabs mid-countdown has stopped paying attention. */
  const onVisibility = (): void => {
    if (typeof document !== 'undefined' && document.hidden) cancelCountdown();
    paint();
  };
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibility);
  }

  const start = el('div');
  const stage = el('div');
  host.append(start, stage);

  const paintStart = (): void => {
    clear(start);
    start.append(el('div', { style: 'display:flex;justify-content:flex-end;margin-bottom:12px' },
      button('Start calling', 'pri', () => void begin())));
  };

  /**
   * Says "still here" about both leads this session is holding.
   *
   * WHY IT EXISTS
   * A lease is four minutes. A real conversation is often longer, and without
   * this the lead falls out from under the agent at minute five — a colleague
   * is handed, in perfectly good faith, the household somebody is mid-sentence
   * with. That is the exact failure the lease exists to prevent, arriving
   * through a different door.
   *
   * Every ninety seconds, so two consecutive failures still leave room before a
   * four-minute lease lapses. `lost` is set when the server says the lead now
   * belongs to somebody else, and the screen says so rather than letting an
   * agent finish a call on a record they no longer hold.
   */
  async function keepHold(): Promise<void> {
    if (!running) return;
    for (const held of [current, ahead]) {
      if (!held) continue;
      try {
        const r = await post<{ heldUntil: string | null }>('/v1/dial/hold', { leadId: held.id });
        if (r.heldUntil === null && held === current) { lost = true; paint(); }
      } catch { /* one missed heartbeat is not a lost lead; the next one tries again */ }
    }
  }

  /** Reads the gate's own account of what is stopping the blocked leads. */
  async function loadBlocked(): Promise<void> {
    try {
      const q = await get<Queue>('/v1/leads/queue?limit=1');
      blockedBy = q.blockedBy ?? [];
    } catch { blockedBy = []; }
  }

  /**
   * The honest half of an empty queue.
   *
   * A licence that is not on file is the one cause an agent cannot wait out and
   * cannot fix themselves, so it is named first and it names the screen the fix
   * lives on. Everything else is listed underneath, biggest cause first, in the
   * gate's own words.
   */
  function blockedNote(): HTMLElement | null {
    if (!blockedBy.length) return null;
    const licence = blockedBy.find((b) => b.code === 'licence');
    if (licence) {
      return el('div', { class: 'warnbox', style: 'margin-bottom:14px' },
        el('b', null, 'You are not licensed on file for these leads. '),
        `${licence.leads.toLocaleString()} of them are in a state with no active licence `
        + 'recorded against your name. This is not something waiting will fix — somebody with '
        + 'manager access has to add your licence under Settings → People, and it takes a '
        + 'minute. Until then this queue stays empty however many leads the agency has.');
    }
    const top = blockedBy[0];
    if (!top) return null;
    return el('div', { class: 'note', style: 'margin-bottom:14px' },
      el('b', null, `${top.leads.toLocaleString()} blocked — ${top.check.toLowerCase()}. `),
      top.message,
      blockedBy.length > 1
        ? ` (${blockedBy.length - 1} other reason${blockedBy.length === 2 ? '' : 's'} as well.)`
        : '');
  }

  /** Claims the next name in the background, so the hand-off is instant. */
  async function claimAhead(): Promise<void> {
    if (ahead) return;
    try {
      const skip = [...skipped, ...(current ? [current.id] : [])];
      const r = await post<NextResult>('/v1/dial/next', { skip });
      ahead = r.lead;
      /*
       * Its campaign's answer travels WITH it.
       *
       * The pre-claimed name is handed straight to the agent without another
       * trip, so if this were not carried the screen would have to either
       * inherit the previous lead's permission — which is how a preview-only
       * list gets dialled automatically — or refuse, which would switch
       * hands-free off every time somebody skipped.
       */
      aheadAutoDial = r.autoDial ?? false;
      if (r.stopped) stopped = true;
    } catch { ahead = null; aheadAutoDial = false; }
  }

  async function begin(): Promise<void> {
    if (busy) return;
    busy = true;
    try {
      const r = await post<NextResult>('/v1/dial/next', { skip: [] });
      if (!r.lead) {
        toast(r.stopped
          ? 'All calling has been stopped for the agency.'
          : 'Nothing is callable right now.', r.stopped ? 'crit' : 'info');
        return;
      }
      running = true;
      current = r.lead;
      mayAutoDial = r.autoDial ?? false;
      unattended = 0;
      quoting = false;
      stats.dials = 0;
      stats.spoke = 0;
      stats.startedAt = Date.now();
      clear(start);
      hooks.onStart();
      bindKeys();
      holdTimer = setInterval(() => { void keepHold(); }, 90_000);
      paint();
      maybeAutoDial();
      void claimAhead();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not start a calling session.', 'crit');
    } finally { busy = false; }
  }

  async function finish(): Promise<void> {
    running = false;
    unbindKeys();
    if (holdTimer) { clearInterval(holdTimer); holdTimer = null; }
    // Before anything awaits. A countdown that survived the end of the session
    // would place a call onto a screen nobody is looking at any more, which is
    // the one outcome this whole feature has to make impossible.
    cancelCountdown();
    mayAutoDial = false;
    aheadAutoDial = false;
    unattended = 0;
    endCallWatch();
    /*
     * Hand back everything still held. Without this an agent who stops for
     * lunch leaves two names invisible to the rest of the floor for four
     * minutes — harmless once, and on a small shared book, noticeable.
     */
    for (const held of [current, ahead]) {
      if (held) {
        try { await post('/v1/dial/release', { leadId: held.id }); } catch { /* the lease lapses anyway */ }
      }
    }
    current = null;
    ahead = null;
    quoting = false;
    skipped.length = 0;
    clear(stage);
    paintStart();
    hooks.onStop();
  }

  /** Records the outcome and moves on, without a page ever reloading. */
  async function dispose(code: string, callbackAt?: string): Promise<void> {
    if (!running || busy || !current) return;
    if (code === 'do_not_contact'
      && !confirm('Add this number to the agency do-not-call list?\n\n'
        + 'This is permanent, applies to everyone in the agency, and is the right '
        + 'thing to do if they asked. It cannot be undone from this screen.')) return;

    /*
     * A callback is the only outcome that needs a second piece of information,
     * and it is the one where losing it costs the most. So the screen stops and
     * asks instead of moving on — but it does not REFUSE, because an outcome
     * that cannot be recorded is an outcome that goes unrecorded, and a
     * callback with no time still beats a call nobody wrote down.
     */
    if (code === 'callback' && callbackAt === undefined) { askWhen(); return; }

    // The agent did something, so somebody is here. This is the ONLY thing that
    // clears the unattended count: a disposition is the one action that proves a
    // person worked the call rather than watched the machine place it.
    unattended = 0;
    cancelCountdown();

    busy = true;
    const done = current;
    try {
      /*
       * THROUGH THE CALL WHEN THERE IS ONE.
       *
       * `/v1/dial/:id/outcome` closes the session, puts the telephone down and
       * writes the disposition onto the lead — the same `LeadService.disposition`
       * the other path uses, so nothing about the lead is recorded differently.
       * Going straight to the lead route instead, as this did, left the call
       * session open and the carrier leg live: the record said the call was
       * still in progress while the agent was two names further down, and the
       * line stayed connected and metered until the consumer hung up.
       *
       * It falls back the moment there is no platform call — an agency dialling
       * from the number on the card has no session to close, and that is the
       * ordinary case, not an edge one.
       */
      // Whatever was tapped during the call rides along. It costs the agent
      // nothing here, which is the whole reason it was captured earlier.
      const why = whyNot ? { lossReason: whyNot } : {};
      if (call && !call.endedAt) {
        await post(`/v1/dial/${call.sessionId}/outcome`, {
          disposition: code,
          ...why,
          ...(callbackAt ? { note: `Callback set for ${callbackAt}` } : {}),
        });
        if (callbackAt) {
          await post(`/v1/leads/${done.id}/disposition`, { disposition: code, callbackAt, ...why });
        }
      } else {
        await post(`/v1/leads/${done.id}/disposition`, {
          disposition: code,
          ...why,
          ...(callbackAt ? { callbackAt } : {}),
        });
      }

      /*
       * AND THE PROMISE ITSELF, which is not the same thing as the callback
       * time on the lead.
       *
       * `callbackAt` holds one date and moves the queue. A promise says WHO
       * gave their word, at what SPECIFIC time, and what the person said they
       * would be doing beforehand — and it is the only one of the two that can
       * ever answer "did we keep it".
       *
       * Failing here does not cost the agent their outcome. That is already
       * recorded, they have moved on, and the queue still holds the lead at the
       * right moment through `callbackAt` — which is the half that matters most.
       */
      if (callbackAt && (code === 'callback' || code === 'appointment')) {
        try {
          await post('/v1/promises', {
            leadId: done.id,
            kind: code === 'appointment' ? 'appointment' : 'callback',
            dueAt: callbackAt,
            ...(theirPlan.trim() ? { theirPlan: theirPlan.trim() } : {}),
          });
        } catch { /* see above — never at the cost of the disposition */ }
      }
      stats.dials += 1;
      if (SPOKE.has(code)) stats.spoke += 1;
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not record that outcome.', 'crit');
      busy = false;
      paint();
      return;
    }

    try { await post('/v1/dial/release', { leadId: done.id }); } catch { /* it lapses anyway */ }
    current = ahead;
    ahead = null;
    mayAutoDial = aheadAutoDial;
    aheadAutoDial = false;
    lost = false;
    quoting = false;
    endCallWatch();
    busy = false;
    // The prefetch already learned the floor had been stopped; act on it now
    // rather than at the next empty queue, which could be minutes away.
    if (stopped) { paint(); return; }

    if (!current) {
      await advance();
      return;
    }
    paint();
    // This is the hand-off that makes the floor hands-free: an outcome was just
    // recorded and the next telephone starts ringing without a press.
    maybeAutoDial();
    void claimAhead();
  }

  /** Pulls the next name when nothing was waiting. */
  async function advance(): Promise<void> {
    busy = true;
    lost = false;
    quoting = false;
    endCallWatch();
    paint();
    try {
      const r = await post<NextResult>('/v1/dial/next', { skip: skipped });
      current = r.lead;
      stopped = r.stopped;
      mayAutoDial = r.autoDial ?? false;
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not load the next lead.', 'crit');
      current = null;
      mayAutoDial = false;
    } finally { busy = false; }
    /*
     * Nothing to call. Find out WHY before drawing the empty screen — this is
     * the moment an agent decides whether to wait or to ask somebody, and
     * "everything is resting" versus "your licence is not on file" are
     * completely different answers.
     */
    if (!current && !stopped) await loadBlocked();
    paint();
    // The whole of hands-free: a new name has landed, so start the clock. It
    // refuses itself if anything says it should not run.
    maybeAutoDial();
    if (current) void claimAhead();
  }

  async function skip(): Promise<void> {
    if (!running || busy || !current) return;
    const done = current;
    skipped.push(done.id);
    lost = false;
    quoting = false;
    unattended = 0;
    cancelCountdown();
    endCallWatch();
    try { await post('/v1/dial/release', { leadId: done.id }); } catch { /* lapses */ }
    current = ahead;
    ahead = null;
    mayAutoDial = aheadAutoDial;
    aheadAutoDial = false;
    if (!current) { await advance(); return; }
    paint();
    maybeAutoDial();
    void claimAhead();
  }

  /**
   * "When?" — the four buttons that cover almost every real answer.
   *
   * WHY BUTTONS AND NOT A DATE PICKER FIRST
   * The agent is mid-sentence with somebody on the line. "Tomorrow morning" has
   * to be one tap, or it becomes a note that never turns into a callback.
   * Anything unusual still gets a proper field underneath, but the four presets
   * are what will actually be pressed.
   */
  function askWhen(): void {
    const at = (fn: (d: Date) => void): string => {
      const d = new Date();
      fn(d);
      return d.toISOString();
    };
    const inHours = (h: number): string => at((d) => d.setHours(d.getHours() + h));
    const tomorrowAt = (hour: number): string => at((d) => {
      d.setDate(d.getDate() + 1);
      d.setHours(hour, 0, 0, 0);
    });

    const field = input({ type: 'datetime-local' });
    const choices = el('div', { style: 'display:flex;flex-wrap:wrap;gap:8px;margin:12px 0' },
      button('In an hour', 'sm', () => void dispose('callback', inHours(1))),
      button('This afternoon', 'sm', () => void dispose('callback', at((d) => d.setHours(15, 0, 0, 0)))),
      button('Tomorrow morning', 'sm', () => void dispose('callback', tomorrowAt(10))),
      button('Tomorrow evening', 'sm', () => void dispose('callback', tomorrowAt(18))));

    /*
     * "And what will you be doing just before?"
     *
     * THE BEST-EVIDENCED CONTROL ON THIS ENTIRE SCREEN, and it looks like a
     * throwaway. Nickerson & Rogers (Psychological Science 2010) ran this on
     * 287,228 people: a script asking when, where and what you would be doing
     * beforehand moved real follow-through +4.1pp among those reached, while a
     * plain reminder moved -0.1pp — nothing. Milkman et al. (PNAS 2011) found
     * the same shape: a date alone did not work, a date and a time did.
     *
     * So it is optional, deliberately unlabelled as "notes", and phrased as the
     * question the agent should actually ask out loud. The answer is stored so
     * this agency can measure kept-rate with a plan against without — which
     * would be the first measurement of the effect in phone sales anywhere.
     */
    const plan = input({ type: 'text' });
    plan.placeholder = 'e.g. "after I get back from church"';
    plan.value = theirPlan;
    plan.oninput = () => { theirPlan = plan.value; };

    const box = el('div', { class: 'note', style: 'margin-top:14px' },
      el('b', null, 'When did they ask you to ring back? '),
      'Until this time they are held out of everybody\'s queue; after it they go to the front '
      + 'of yours.',
      choices,
      el('div', { style: 'margin:10px 0 6px' },
        el('div', {
          style: 'font-size:12px;color:var(--ink-3);margin-bottom:4px',
          text: 'Ask them: "what will you be doing just before then?" — people who say it out loud turn up.',
        }),
        plan),
      el('div', { style: 'display:flex;gap:8px;align-items:center;flex-wrap:wrap' },
        field,
        button('Set it', 'sm pri', () => {
          if (!field.value) { toast('Pick a date and time first.', 'info'); return; }
          void dispose('callback', new Date(field.value).toISOString());
        }),
        /*
         * "Later today" rather than "skip the time", because a callback with no
         * hour on it is not a promise — nobody named a time, so there is
         * nothing to keep, and it comes back as an ordinary retry with no lift.
         * Naming a real default here is more honest than a blank that quietly
         * becomes one, and it is one fewer thing for an agent mid-call to think
         * about.
         */
        button('Later today', 'sm', () => void dispose('callback', inHours(4)))));

    // Under the buttons, replacing nothing, so the lead is still on screen
    // while they answer.
    stage.append(box);
    field.focus();
  }

  function bindKeys(): void {
    unbindKeys();
    keyHandler = (e: KeyboardEvent) => {
      if (!running) return;
      const target = e.target as HTMLElement | null;
      // Never steal a keystroke from something a person is typing into.
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const hit = QUICK.find((d) => d.key === e.key);
      if (hit) { e.preventDefault(); void dispose(hit.code); return; }
      if (e.key === 's' || e.key === 'S') { e.preventDefault(); void skip(); return; }
      /*
       * Q for a price. It is the only shortcut that is not an outcome, and it
       * is the one that gets pressed at the moment the call becomes worth
       * something — so it has to be a key, not a hunt for a button.
       */
      if (e.key === 'q' || e.key === 'Q') { e.preventDefault(); openQuote(); return; }
      if (e.key === 'Escape') {
        e.preventDefault();
        // Escape closes the price panel first. Otherwise an agent who opened it
        // to check a number ends their session trying to get rid of it.
        if (quoting) { quoting = false; paint(); return; }
        void finish();
      }
    };
    document.addEventListener('keydown', keyHandler);
  }

  function unbindKeys(): void {
    if (keyHandler) document.removeEventListener('keydown', keyHandler);
    keyHandler = null;
  }

  function paint(): void {
    clear(stage);
    if (!running) return;

    const mins = Math.max(1, (Date.now() - stats.startedAt) / 60_000);
    const bar = el('div', { class: 'tiles', style: 'margin-bottom:14px' },
      tileOf(String(stats.dials), 'Dials this session', `${Math.round(stats.dials / mins * 60)} an hour`, 'var(--iris)'),
      tileOf(String(stats.spoke), 'People you spoke to',
        // Null, not nought. Nobody has a contact rate before their first call,
        // and showing 0% on dial one reads as catastrophe.
        stats.dials ? `${Math.round((stats.spoke / stats.dials) * 100)}% of dials` : 'no dials yet',
        'var(--good)'),
      tileOf(String(Math.round(mins)), 'Minutes in', 'since you pressed start', 'var(--copper)'));

    /*
     * The hands-free switch, and the one message that has to be unmissable.
     *
     * `stalled` is the failsafe firing: two auto-dials went out and the agent
     * recorded nothing on either. The likeliest explanation is that nobody is
     * sitting here — and every further call would connect a household to
     * silence, which is an abandoned call. So it stops, and it says why in
     * words rather than by quietly not dialling, because a machine that goes
     * silent is indistinguishable from one that is broken.
     */
    const stalled = handsFree && mayAutoDial && unattended >= UNATTENDED_LIMIT;
    const toggle = el('label', {
      style: 'display:inline-flex;gap:8px;align-items:center;font-size:13px;cursor:pointer',
      title: 'The platform dials the next name on its own. One call at a time — never more.',
    });
    const box = el('input', { type: 'checkbox' }) as HTMLInputElement;
    box.checked = handsFree;
    box.addEventListener('change', () => setHandsFree(box.checked));
    toggle.append(box, el('span', { text: 'Dial the next name automatically' }));

    const handsFreeRow = el('div', {
      style: 'display:flex;gap:12px;align-items:center;flex-wrap:wrap;margin-bottom:12px',
    }, toggle,
    !handsFree ? null
      : !mayAutoDial
        ? el('span', { class: 'qhour', text: 'this list is set to preview — press Call' })
        : stalled
          ? el('span', { style: 'color:var(--crit);font-weight:600', text: 'paused — nothing was recorded on the last two calls' })
          : el('span', { class: 'qhour', text: 'hands-free · one call at a time' }),
    stalled
      ? button('I am here — carry on', 'sm pri', () => { unattended = 0; maybeAutoDial(); paint(); })
      : null);

    if (!current && stopped) {
      /*
       * THE KILL SWITCH HAS TO REACH THE FLOOR.
       *
       * It exists for the moment something has gone wrong that no rule
       * anticipated, and the whole point of it is that every phone in the
       * building stops. But an agent inside a session is on the one screen that
       * has stopped polling anything: the gate refuses every lead, so without
       * this the session says "nothing callable right now" and the agent shrugs
       * and waits, in the middle of exactly the incident it was thrown for.
       *
       * A stop that people have to be told about by somebody walking over is
       * not a kill switch.
       */
      stage.append(panel('Calling session', bar,
        el('div', { class: 'warnbox' },
          el('b', null, 'All calling has been stopped for the agency. '),
          'Somebody has thrown the hold — a bad list, a complaint, something found in a '
          + 'recording. Put the phone down. An owner or administrator has to lift it before '
          + 'anybody calls again.'),
        el('div', { style: 'margin-top:12px' }, button('End the session', 'pri', () => void finish()))));
      return;
    }

    if (!current) {
      stage.append(panel('Calling session', bar,
        /*
         * WHY THIS IS NOT ONE SENTENCE ANY MORE.
         *
         * It used to say "this will not stay true for long", which is true when
         * the queue is empty because people are asleep and FALSE when it is
         * empty because nobody has entered this agent's licence. A newly
         * licensed floor arriving on its first morning hits the second case, all
         * ten of them separately, and every one of them concludes the software
         * is broken. It is not: their licence is not on file, and this is the
         * only screen positioned to say so.
         *
         * `blockedBy` already carries the gate's own answer for every blocked
         * row. It was simply never read here.
         */
        blockedNote(),
        empty('Nothing callable right now',
          'Everything in front of you has been worked, is resting after a recent call, is being '
          + 'worked by somebody else, or is waiting on the hour where those people live.'),
        el('div', { style: 'margin-top:12px;display:flex;gap:8px' },
          button('Look again', 'sm', () => void advance()),
          button('Stop', 'sm', () => void finish()))));
      return;
    }

    const lead = current;
    const digits = (lead.phone ?? '').replace(/\D/g, '');
    /*
     * Said out loud rather than swallowed. If the lease lapsed and a colleague
     * has taken this name, the agent needs to know before they dial — finishing
     * a call on a record somebody else is also working is the one thing the
     * lease exists to prevent, and hiding it would make the screen complicit.
     */
    const stolen = lost
      ? el('div', { class: 'warnbox', style: 'margin-bottom:14px' },
        el('b', null, 'Somebody else has picked this person up. '),
        'Your hold on them lapsed — probably a long call or a dropped connection. Skip to the '
        + 'next name rather than ringing them as well.')
      : null;
    const hour = lead.localHour === null ? null : Math.floor(lead.localHour);

    const buttons = el('div', {
      style: 'display:flex;flex-wrap:wrap;gap:8px;margin-top:16px',
    });
    for (const d of QUICK) {
      const b = button(`${d.key}  ${d.label}`, `sm ${d.tone}`.trim(), () => void dispose(d.code));
      b.disabled = busy;
      buttons.append(b);
    }

    stage.append(panel('Calling session', bar,
      handsFreeRow,
      stolen,
      el('div', { class: 'callcard' },
        el('div', { style: 'display:flex;flex-wrap:wrap;gap:12px;align-items:baseline' },
          el('div', { style: 'font-size:26px;font-weight:600', text: fullName(lead) }),
          /*
           * First thing after the name, because it changes how the call opens.
           * "You asked me to ring you back" is a different conversation from a
           * cold introduction, and an agent who does not know which one they
           * are in has already lost the advantage of it.
           */
          (lead.rankParts?.callback ?? 0) > 0 ? chip('they asked for this call', 'ok') : null,
          lead.state ? chip(lead.state) : null,
          hour === null ? null : el('span', {
            class: 'qhour', title: 'Local time where this person lives',
            text: `${String(hour).padStart(2, '0')}:00 there`,
          }),
          lead.minutesUntilClose > 0 && lead.minutesUntilClose <= 60
            ? chip(`${lead.minutesUntilClose} min left`, 'crit')
            : null),
        // The number, as a link. On a desk phone it is something to read back;
        // on anything with a dialler it places the call.
        el('div', { style: 'margin-top:8px' },
          digits.length === 10
            ? el('a', {
              href: `tel:+1${digits}`,
              style: 'font-size:30px;font-weight:600;letter-spacing:.5px;text-decoration:none;color:var(--iris)',
              text: prettyPhone(lead.phone),
            })
            : el('span', { style: 'font-size:24px;color:var(--ink-3)', text: prettyPhone(lead.phone) }),
          /*
           * Only when there is a number to ring. The `tel:` link above stays
           * whatever happens — it is what an agency with no carrier uses, and
           * it is what anybody uses when the platform's own call fails.
           */
          digits.length === 10
            ? el('span', { style: 'margin-left:14px' },
              call
                ? chip(`calling — ${call.state.replace(/_/g, ' ')}`, 'ok')
                : countdownTimer
                  // Counting down. The button becomes the way to STOP it, not
                  // the way to start — an agent watching a number tick has one
                  // question, and it is "how do I make it wait".
                  ? el('span', { style: 'display:inline-flex;gap:8px;align-items:center' },
                    chip(`dialling in ${countdownLeft}…`, 'ok'),
                    button('Hold', 'sm', () => { cancelCountdown(); paint(); }),
                    button('Now', 'sm pri', () => { cancelCountdown(); unattended += 1; void placeCall(); }))
                  : button(placing ? 'Calling…' : 'Call', 'sm pri', () => void placeCall()))
            : null),
        // Directly under the number, where the eye already is when the call
        // connects. See fileStrip for why each field earns its place.
        fileStrip(lead),
        callCard(),
        historyLine(lead),
        lead.why
          ? el('div', {
            style: 'margin-top:10px;font-size:13px;color:var(--crit)',
            text: `Moved up: ${lead.why}.`,
          })
          : null,
        lead.attempts
          ? el('div', {
            style: 'margin-top:6px;font-size:13px;color:var(--ink-3)',
            text: `${lead.attempts} previous attempt${lead.attempts === 1 ? '' : 's'}.`,
          })
          : null),
      whyNotRow(),
      buttons,
      el('div', { style: 'display:flex;gap:8px;margin-top:14px;align-items:center;flex-wrap:wrap' },
        /*
         * The one button that is not an outcome. It is here rather than on the
         * quoting page because of what leaving costs: the lease heartbeat
         * stops, the prefetched next name is dropped, the shortcuts unbind, and
         * the agent arrives at a form asking for three things that are on the
         * card they just left. See quoteHere.
         */
        button('Q  Price it', 'sm pri', () => openQuote()),
        button('S  Skip', 'sm', () => void skip()),
        button('Esc  Stop', 'sm', () => void finish()),
        el('span', {
          style: 'font-size:12px;color:var(--ink-4)',
          text: ahead ? 'Next one is loaded.' : 'Loading the next one…',
        }))));

    if (quoting) {
      quoteHere(lead, stage, {
        // A saved quote is not an outcome. The agent is still talking to them,
        // and what happens next — sale, callback, thinking about it — is a
        // separate keystroke they have not pressed yet.
        onSaved: () => { quoting = false; paint(); },
        onClose: () => { quoting = false; paint(); },
      });
    }
  }

  /**
   * Place the call, through the platform, on this agency's telephony account.
   *
   * WHY IT IS TWO REQUESTS AND NOT ONE
   * The first mints a signed, single-use authorisation: it is the moment the
   * compliance gate runs — consent, the do-not-call registry, the calling
   * window in this person's own time zone, the weekly attempt cap, the agency
   * hold. The second spends it. Nothing can place a call without one, and one
   * cannot be spent twice, so the record of what was permitted and the record of
   * what happened are the same record.
   *
   * It is deliberately possible to work the whole day without ever pressing
   * this. An agency with no carrier configured still has the number on the card
   * and dials it however they always have, and every other screen is unchanged.
   */
  async function placeCall(): Promise<void> {
    if (!current || placing || call) return;
    placing = true;
    paint();
    try {
      const auth = await post<{
        ok?: boolean;
        failures?: Array<{ message: string }>;
        authorization?: unknown;
      }>('/v1/dial/authorize', { leadId: current.id });

      if (auth.ok === false || !auth.authorization) {
        /*
         * The gate said no. Its own words, not a summary — "it is 7:40am where
         * they live" is actionable and "cannot call" is not, and an agent who
         * is told why once learns the rule.
         */
        const why = (auth.failures ?? []).map((f) => f.message).join(' ');
        toast(why || 'That number cannot be called right now.', 'crit');
        return;
      }

      const placed = await post<{ id: string; state: string }>('/v1/dial/attempt', {
        authorization: auth.authorization,
      });
      call = {
        sessionId: placed.id, state: placed.state, endedAt: null,
        transferToUserId: null, disclosure: null,
      };
      toast('Calling.', 'ok');
      watchCall();
      void loadTargets();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'The call could not be placed.', 'crit');
    } finally {
      placing = false;
      paint();
    }
  }

  /**
   * Keeps the card telling the truth about a call nobody here is controlling.
   *
   * WHY IT POLLS
   * The carrier moves the call — it rings, it is answered, it goes to voicemail,
   * they hang up — and every one of those arrives as a webhook to the server,
   * not to this browser. Without this, the chip would say "dialing" for the rest
   * of the session no matter what actually happened, which is worse than showing
   * nothing: an agent would read it and believe it.
   *
   * Two and a half seconds, and it stops the moment the call reaches a state it
   * cannot leave. A poll that runs on after the call is over is a request per
   * agent per two seconds, for ever, for nothing.
   */
  function watchCall(): void {
    if (callTimer) clearInterval(callTimer);
    callTimer = setInterval(() => { void refreshCall(); }, 2_500);
  }

  function stopWatching(): void {
    if (callTimer) { clearInterval(callTimer); callTimer = null; }
  }

  /**
   * Everything about the call on screen, gone.
   *
   * Called wherever the agent leaves a lead — a disposition, a skip, the next
   * name, stopping. Forgetting the timer here would leave a poll running
   * against a finished call for the rest of the session, one request every two
   * and a half seconds, per agent, for nothing.
   */
  function endCallWatch(): void {
    stopWatching();
    call = null;
    targets = null;
    choosing = false;
    whyNot = null;
    theirPlan = '';
  }

  async function refreshCall(): Promise<void> {
    const live = call;
    if (!live) { stopWatching(); return; }
    try {
      const r = await get<LiveCall & { id: string }>(`/v1/dial/${live.sessionId}`);
      // Guard against the reply for a call the agent has already moved past —
      // dispositions clear `call`, and a late response must not resurrect it.
      if (!call || call.sessionId !== live.sessionId) return;
      const was = call.state;
      call = {
        sessionId: live.sessionId,
        state: r.state,
        endedAt: r.endedAt ?? null,
        transferToUserId: r.transferToUserId ?? null,
        disclosure: r.disclosure ?? null,
      };
      if (r.endedAt) stopWatching();
      if (r.state !== was) paint();
    } catch {
      /*
       * One failed poll is not a dead call. The screen keeps the last state it
       * knew and tries again — and the agent has the person on the line either
       * way, which is the thing that actually matters.
       */
    }
  }

  async function loadTargets(): Promise<void> {
    const live = call;
    if (!live) return;
    try {
      const r = await get<{ targets: TransferTarget[] }>(`/v1/dial/${live.sessionId}/transfer-targets`);
      if (call?.sessionId !== live.sessionId) return;
      targets = r.targets;
      if (choosing) paint();
    } catch { targets = null; }
  }

  /**
   * Moves the call, and says so plainly when the server refuses.
   *
   * Every refusal here is a real one worth reading out: a disclosure that was
   * not named, an agent who is not licensed in that state, a transfer the
   * carrier could not place. The screen never moves the chip on its own — it
   * takes the state the server returns, so what is on screen is what is in the
   * record.
   */
  async function moveCall(to: string, extra: Record<string, unknown> = {}): Promise<void> {
    const live = call;
    if (!live || busy) return;
    busy = true;
    paint();
    try {
      const r = await post<{ state: string; endedAt: string | null; transferToUserId: string | null }>(
        `/v1/dial/${live.sessionId}/transition`, { to, ...extra },
      );
      if (call?.sessionId === live.sessionId) {
        call = {
          ...call,
          state: r.state,
          endedAt: r.endedAt ?? null,
          transferToUserId: r.transferToUserId ?? null,
        };
        if (r.endedAt) stopWatching();
      }
      choosing = false;
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'That did not go through.', 'crit');
    } finally {
      busy = false;
      paint();
    }
  }

  /**
   * The live call, once this platform placed one.
   *
   * WHAT IT IS FOR
   * Pressing Call used to be the end of the screen's involvement: the chip said
   * "dialing" and stayed saying it, and every route that moves a call — the
   * disclosure, the conversation, the transfer to a licensed human, the ending
   * — was built, tested, and reachable by nothing a person could click. This is
   * the floor those routes were waiting for.
   *
   * WHY IT IS ONE ROW OF BUTTONS AND NOT A CONSOLE
   * The agent is talking to somebody. Every control here is one press, in the
   * order a call actually goes, and only the ones that are legal from where the
   * call is now are shown — a screen offering `Transfer` on a call nobody has
   * answered is a screen that teaches an agent to ignore it.
   *
   * WHAT IT WILL NOT DO
   * Move the chip on its own. Every state on screen came back from the server,
   * so what an agent reads is what is in the record — including when the carrier
   * moved the call and nobody here pressed anything.
   */
  function callCard(): HTMLElement | null {
    const live = call;
    if (!live) return null;

    const ended = !!live.endedAt;
    const label = live.state.replace(/_/g, ' ');
    const tone = ended ? (live.state === 'completed' ? 'ok' : 'warn') : 'ok';

    const acts: HTMLElement[] = [];
    const act = (text: string, to: string, extra?: Record<string, unknown>): void => {
      acts.push(button(text, 'sm', () => void moveCall(to, extra ?? {})));
    };

    /*
     * The disclosure is a legal precondition for the conversation, not a step
     * in it, so it is shown as the wording to READ rather than as a button
     * labelled with a state. The version recorded is the version on screen —
     * see `liveCall` on the server for why the browser must not choose it.
     */
    let disclosureBox: HTMLElement | null = null;
    if (!ended && live.state === 'answered' && live.disclosure) {
      const d = live.disclosure;
      disclosureBox = el('div', { class: 'note', style: 'margin-top:10px' },
        el('b', null, 'Read this first: '),
        el('span', { text: d.text }),
        el('div', { style: 'margin-top:8px' },
          button('I read it', 'sm pri', () => void moveCall('disclosed', { disclosureVersion: d.version }))));
    }

    if (!ended) {
      switch (live.state) {
        case 'dialing':
        case 'ringing':
          act('They picked up', 'answered');
          break;
        case 'answered':
          // The disclosure box above carries the only move out of here.
          break;
        case 'disclosed':
          act('Talking', 'conversing');
          break;
        case 'conversing':
          acts.push(button(choosing ? 'Choosing…' : 'Transfer to an agent', 'sm pri', () => {
            choosing = !choosing;
            if (choosing && targets === null) void loadTargets();
            paint();
          }));
          break;
        case 'transferring':
          /*
           * Handing them back is a real move, not a failure state — the agent
           * leg rang out and the person is still on the line. It is here because
           * the alternative, when a colleague does not pick up, is silence.
           */
          act('Take them back', 'conversing', { reason: 'handed back at the desk' });
          break;
        default:
          break;
      }
      // Available from anywhere a call is live, because a call can end at any
      // point and an agent should never have to reach for the disposition keys
      // to get a line to hang up.
      acts.push(button('Hang up', 'sm', () => void moveCall(
        live.state === 'bridged' || live.state === 'conversing' || live.state === 'disclosed'
          ? 'completed'
          : 'failed',
        { reason: 'ended at the desk' },
      )));
    }

    const targetBox = choosing && !ended ? transferBox() : null;

    return el('div', { style: 'margin-top:12px' },
      el('div', { style: 'display:flex;gap:8px;align-items:center;flex-wrap:wrap' },
        chip(`on the call — ${label}`, tone),
        ...acts),
      disclosureBox,
      targetBox);
  }

  /**
   * "Why not?" — one tap, while they are still talking.
   *
   * THE FIELD EVERY DIALER IN THIS INDUSTRY IS MISSING, and the reason they are
   * missing it is visible right here: there is nowhere obvious to put it. Their
   * disposition lists answer what happened to the CALL. Not one of them answers
   * what happened to the SALE, so "not interested" swallows most conversations
   * and the question an agency owner actually has cannot be asked.
   *
   * WHY IT IS ON THE CARD AND NOT IN A DIALOG AFTER THE DISPOSITION
   * A dialog after the fact turns one keystroke into two at the moment an agent
   * is trying to reach the next name, and a capture that costs a second at the
   * wrong moment fills with whatever clears the screen. Here it is tapped when
   * the reason is HEARD — the person is mid-sentence explaining it — and by the
   * time the disposition is pressed the answer is already recorded.
   *
   * NOTHING IS REQUIRED. An outcome that cannot be recorded goes unrecorded,
   * which is worse than one recorded without a reason. How often it is skipped
   * is measured instead: a floor leaving it blank a third of the time has a
   * screen problem, and that is this screen's problem to fix, not the agents'.
   */
  function whyNotRow(): HTMLElement | null {
    if (!current) return null;
    const chips = WHY_NOT.map((r) => button(
      r.label,
      whyNot === r.code ? 'sm pri' : 'sm',
      () => { whyNot = whyNot === r.code ? null : r.code; paint(); },
    ));
    return el('div', {
      style: 'margin-top:12px;padding-top:10px;border-top:1px solid var(--line)',
    },
    el('div', {
      style: 'font-size:12px;color:var(--ink-4);margin-bottom:6px',
      text: whyNot
        ? 'Recorded. It goes with whatever outcome you press.'
        : 'If they say no — why? One tap, now, while they are telling you.',
    }),
    el('div', { style: 'display:flex;flex-wrap:wrap;gap:6px' }, ...chips));
  }

  /**
   * Who can take this call.
   *
   * EVERY LICENSED COLLEAGUE, NOT ONLY THE AVAILABLE ONES. At ten past six the
   * available list is often empty, and an empty list with no explanation reads
   * as a broken screen. Somebody who has not marked themselves available is
   * shown and can be picked — that is a judgement call, and it is audited on the
   * server — but the screen says which is which rather than pretending.
   *
   * Somebody with no telephone number cannot be picked at all: a transfer RINGS
   * them, so the refusal is certain, and finding that out mid-conversation is
   * the worst possible moment.
   */
  function transferBox(): HTMLElement {
    if (targets === null) {
      return el('div', { class: 'note', style: 'margin-top:10px', text: 'Finding who can take it…' });
    }
    if (!targets.length) {
      return el('div', { class: 'note', style: 'margin-top:10px' },
        el('b', null, 'Nobody is licensed for this state. '),
        'A transfer has to go to somebody licensed where this person lives. Keep the call, or '
        + 'book a time with an agent who is.');
    }
    const rows = targets.map((t) => el('div', {
      style: 'display:flex;gap:8px;align-items:center;margin-top:6px;flex-wrap:wrap',
    },
    el('span', { style: 'min-width:160px', text: t.name }),
    t.available ? chip('at their desk', 'ok') : chip(`last seen ${t.lastSeenAt ? ago(t.lastSeenAt) : 'a while ago'}`, 'warn'),
    t.isOwner ? chip('theirs', 'info') : null,
    t.reachable
      ? button('Transfer', 'sm pri', () => void moveCall('transferring', { transferToUserId: t.id }))
      : chip('no number on file', 'crit')));

    return el('div', { class: 'note', style: 'margin-top:10px' },
      el('b', null, 'Hand the call to: '),
      el('div', null, ...rows),
      el('div', { style: 'margin-top:8px' },
        button('Never mind', 'sm', () => { choosing = false; paint(); })));
  }

  /** Opens the pricing panel under the card, without disturbing the session. */
  function openQuote(): void {
    if (!current) return;
    quoting = true;
    paint();
  }

  paintStart();
}

/* ------------------------------------------------------- what the file says -- */

/**
 * The household, as the vendor sold it, on one line under the phone number.
 *
 * WHY IT IS A STRIP AND NOT A PANEL
 * It has to be readable in the half second between the ring and the hello, at
 * the top of the same card as the name. A section the agent has to scroll to,
 * or open, is a section that gets read after the call.
 *
 * WHY EACH ONE IS THERE
 *   age            the first input to every premium in this industry
 *   tobacco        the second, and it moves the price by half again
 *   cover asked    what they said they wanted before anybody rang them
 *   already has    the number that turns a sale into a replacement conversation
 *   dependents     who the money is for, which is the whole pitch
 *   enquired about what they thought they were signing up for
 *
 * Nothing is invented and nothing is inferred. A field the file did not carry
 * is simply absent, because a strip that shows "Tobacco: no" for a lead nobody
 * ever asked is worse than one that shows nothing: the agent believes it, and
 * quotes a non-smoker rate to a smoker.
 */
function fileStrip(lead: QueueLead): HTMLElement | null {
  const bits: HTMLElement[] = [];
  const add = (k: string, v: string, title?: string): void => {
    bits.push(el('span', {
      class: 'filebit',
      ...(title ? { title } : {}),
    }, el('span', { class: 'fk', text: k }), el('span', { class: 'fv', text: v })));
  };

  const age = ageFrom(lead.dob);
  if (age !== null) add('Age', String(age), `Date of birth on file: ${lead.dob}`);
  if (lead.tobacco !== null) add('Tobacco', lead.tobacco ? 'yes' : 'no');
  if (lead.requestedFaceCents !== null) add('Asked for', dollars(lead.requestedFaceCents));
  if (lead.existingCoverageCents !== null) add('Already has', dollars(lead.existingCoverageCents));
  if (lead.dependents !== null) add('Dependents', String(lead.dependents));
  if (lead.coverageInterest) add('Enquired about', lead.coverageInterest);

  if (!bits.length) return null;
  return el('div', { class: 'filestrip' }, ...bits);
}

/**
 * What happened the last time somebody rang this house.
 *
 * Kept apart from the strip above because it is a different kind of fact — the
 * strip is what the vendor said, this is what the agency found out — and
 * because it is the one thing that changes how the call OPENS. Ringing
 * somebody for the fourth time and opening as though it were the first is the
 * fastest way to be hung up on.
 */
function historyLine(lead: QueueLead): HTMLElement | null {
  const parts: string[] = [];
  if (lead.lastDisposition) parts.push(`last time: ${lead.lastDisposition.replace(/_/g, ' ')}`);
  if (!parts.length && !lead.notes) return null;
  return el('div', { class: 'filenote' },
    parts.length ? el('span', { text: parts.join(' · ') }) : null,
    lead.notes
      ? el('span', {
        // Truncated on screen, whole thing in the tooltip. A note that pushes
        // the buttons below the fold costs more than it tells anybody.
        title: lead.notes,
        text: `${parts.length ? ' — ' : ''}${lead.notes.slice(0, 160)}${lead.notes.length > 160 ? '…' : ''}`,
      })
      : null);
}

/** A tile, without importing the one the summary builds inline. */
const tileOf = (
  value: string, label: string, note: string | null, accent: string,
): HTMLElement => el('div', { class: 'tile', style: `--accent:${accent}` },
  el('div', { class: 'k', text: label }),
  el('div', { class: 'v', text: value }),
  note ? el('div', { class: 'd', text: note }) : null);

/* ----------------------------------------------- a price without leaving the call -- */

interface QuoteRec {
  carrierId: string; carrierName: string;
  productId: string; productName: string;
  monthlyCents: number; annualCents: number;
  rateClass: string; faceCents: number | null;
  why: string[]; caveats: string[]; notConsidered: number;
}

interface QuoteRun {
  applicant: { age: number; sex: string; tobacco: boolean; state: string; faceCents: number | null };
  bestMonthlyCents: number | null;
  pricedCount: number;
  unpricedCount: number;
  recommendation: QuoteRec | null;
  sections: Array<{
    carrier: { id: string; name: string };
    priced: Array<{
      product: { id: string; name: string };
      prices: Array<{ monthlyCents: number; rateClass: string; faceCents: number | null }>;
    }>;
    bestMonthlyCents: number | null;
  }>;
}

/**
 * "What would that cost me?" — answered on the call, on the same screen.
 *
 * WHY THIS IS NOT A LINK TO THE QUOTING PAGE
 * Because of what happens in the four seconds it takes to get there. The agent
 * leaves the calling session — which means the lease heartbeat stops, the
 * prefetched next name is dropped, and the keyboard shortcuts unbind — and
 * arrives at a form that asks for a date of birth, a state and a face amount
 * that were all on the card they just left. The client hears typing. Then the
 * agent has to find their way back, and the session starts over.
 *
 * A probe walked exactly that. The price itself came back in two milliseconds;
 * everything else about getting to it was the cost.
 *
 * WHAT IT ASKS FOR AND WHY THAT IS THE FLOOR
 * Sex. It is not on a lead record, from any vendor, and it changes the premium
 * by fifteen per cent — so it is a question on the call, like tobacco, and one
 * tap is as short as honest gets. Everything else is filled from the file and
 * shown, so the agent can see what is being priced and correct it if the
 * client says otherwise.
 *
 * WHAT IT WILL NOT DO
 * Invent a premium. Products with no rate card loaded come back counted, not
 * priced — the same rule as the quoting screen, for the same reason: an agent
 * cannot tell a made-up number from a real one, and the family finds out when
 * the policy issues.
 */
function quoteHere(
  lead: QueueLead,
  host: HTMLElement,
  hooks: { onSaved: () => void; onClose: () => void },
): void {
  let sex: 'male' | 'female' | null = null;
  let tobacco: boolean | null = lead.tobacco;
  let run: QuoteRun | null = null;
  let busy = false;

  const faceBox = input({
    type: 'number', min: '1000', step: '1000',
    value: lead.requestedFaceCents === null ? '' : String(Math.round(lead.requestedFaceCents / 100)),
    style: 'width:130px',
  });

  const body = el('div');
  const box = el('div', { class: 'note quotehere' },
    el('div', { style: 'display:flex;justify-content:space-between;align-items:baseline;gap:12px' },
      el('b', null, `Price it for ${fullName(lead)}`),
      button('Close', 'sm', () => hooks.onClose())),
    body);
  host.append(box);

  const price = async (): Promise<void> => {
    if (busy || sex === null) return;
    busy = true;
    paint();
    const face = Number(faceBox.value) * 100;
    try {
      run = await post<QuoteRun>('/v1/quote', {
        // The date of birth, not an age worked out here. One clock.
        ...(lead.dob ? { dob: lead.dob } : {}),
        sex,
        tobacco: tobacco ?? false,
        ...(lead.state ? { state: lead.state } : {}),
        ...(Number.isFinite(face) && face > 0 ? { faceCents: Math.round(face) } : {}),
      });
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not price that.', 'crit');
      run = null;
    } finally { busy = false; }
    paint();
  };

  /**
   * Save the quote, and — when asked — open the application from it in the
   * same breath.
   *
   * WHY THE SECOND HALF IS ONE MORE CALL AND NOT A SCREEN
   * Because the alternative is what a probe watched: the agent leaves, opens
   * the cases screen, and re-types the carrier, the product, the kind of
   * policy, the face amount and the premium — five things the quote they just
   * saved already knows — while the client listens to them type. The kind of
   * policy is the one that bites hardest: a case that loses it defaults to
   * "other", and the agency's commission grid then cannot price the business
   * its own quoter produced.
   */
  const save = async (rec: QuoteRec, andApply = false): Promise<void> => {
    if (!run || busy) return;
    busy = true;
    paint();
    try {
      const saved = await post<{ id: string }>('/v1/quotes', {
        leadId: lead.id,
        carrierId: rec.carrierId,
        productId: rec.productId,
        // From the run, not from this screen. The server resolved the age off
        // the date of birth against the card's own clock; recomputing it here
        // is how a quote and its saved copy come to differ by a year.
        age: run.applicant.age,
        sex: run.applicant.sex,
        tobacco: run.applicant.tobacco,
        ...(lead.state ? { state: lead.state } : {}),
        faceCents: rec.faceCents ?? run.applicant.faceCents ?? 0,
        monthlyCents: rec.monthlyCents,
        rateClass: rec.rateClass,
        source: 'card',
      });
      if (andApply) {
        const made = await post<{ policy: { id: string } }>(`/v1/quotes/${saved.id}/apply`, {});
        toast(`Application opened for ${fullName(lead)}. Nothing to re-type.`, 'ok');
        void made;
      } else {
        toast(`Quote saved against ${fullName(lead)}.`, 'ok');
      }
      hooks.onSaved();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not save that quote.', 'crit');
    } finally { busy = false; paint(); }
  };

  function paint(): void {
    clear(body);

    const age = ageFrom(lead.dob);
    const askRow = el('div', { style: 'display:flex;flex-wrap:wrap;gap:14px;align-items:flex-end;margin:10px 0' },
      el('div', null,
        el('div', { class: 'qlab', text: 'Male or female?' }),
        el('div', { style: 'display:flex;gap:6px' },
          button('Male', `sm ${sex === 'male' ? 'pri' : ''}`.trim(), () => { sex = 'male'; void price(); }),
          button('Female', `sm ${sex === 'female' ? 'pri' : ''}`.trim(), () => { sex = 'female'; void price(); }))),
      el('div', null,
        el('div', { class: 'qlab', text: 'Tobacco?' }),
        el('div', { style: 'display:flex;gap:6px' },
          button('No', `sm ${tobacco === false ? 'pri' : ''}`.trim(), () => { tobacco = false; void price(); }),
          button('Yes', `sm ${tobacco === true ? 'pri' : ''}`.trim(), () => { tobacco = true; void price(); }))),
      el('div', null,
        el('div', { class: 'qlab', text: 'Cover ($)' }),
        el('div', { style: 'display:flex;gap:6px;align-items:center' },
          faceBox,
          button('Reprice', 'sm', () => void price()))));

    body.append(
      age === null
        ? el('div', { class: 'warnbox', style: 'margin-top:10px' },
          el('b', null, 'No date of birth on this record. '),
          'Every premium in this business starts with an age, so nothing can be priced here. '
          + 'Ask them, put it on the record, and this will work.')
        : el('div', { class: 'qsub', text: `Age ${age} from the date of birth on file${lead.state ? `, ${lead.state}` : ''}.` }),
      askRow);

    if (sex === null) {
      body.append(el('div', { class: 'qsub', text: 'Pick male or female and the price comes back on its own.' }));
      return;
    }
    if (busy) { body.append(skeleton(2)); return; }
    if (!run) return;

    const rec = run.recommendation;
    if (!rec) {
      body.append(el('div', { class: 'warnbox' },
        el('b', null, 'Nothing could be priced for this person. '),
        run.unpricedCount > 0
          ? `${run.unpricedCount} product${run.unpricedCount === 1 ? '' : 's'} could write them, but no rate card has been `
            + 'loaded for any of them. Nobody here will invent a premium — use the carrier\'s own tool for this one.'
          : 'No product in the catalogue writes somebody of this age in this state at this face amount.'));
      return;
    }

    body.append(el('div', { class: 'quoteline' },
      el('div', { class: 'qbig', text: `$${(rec.monthlyCents / 100).toFixed(2)}` }),
      el('div', null,
        el('div', { class: 'qname', text: `${rec.carrierName} — ${rec.productName}` }),
        el('div', {
          class: 'qsub',
          text: `a month, ${rec.rateClass}${rec.faceCents ? `, for ${dollars(rec.faceCents)} of cover` : ''}`,
        })),
      el('div', { style: 'display:flex;gap:8px;margin-left:auto;flex-wrap:wrap' },
        button('Save the quote', 'sm', () => void save(rec)),
        /*
         * The one that matters, and it is the primary. An agent who has heard
         * "yes" should be able to open the application without leaving the
         * call, and everything it needs is already on this panel.
         */
        button('Start the application', 'sm pri', () => void save(rec, true)))));

    if (rec.caveats.length) {
      body.append(el('div', { class: 'qcaveat', text: rec.caveats.join(' · ') }));
    }

    /*
     * The runners-up, because "that's a bit much" is the commonest answer to a
     * first number and an agent who has to re-run the quoter to answer it has
     * lost the moment. Three is enough to have somewhere to go and few enough
     * to read while somebody is talking.
     */
    const others: Array<{ label: string; monthlyCents: number; rec: QuoteRec }> = [];
    for (const sec of run.sections) {
      for (const p of sec.priced) {
        for (const pr of p.prices) {
          if (pr.monthlyCents === rec.monthlyCents && p.product.id === rec.productId) continue;
          others.push({
            label: `${sec.carrier.name} — ${p.product.name}`,
            monthlyCents: pr.monthlyCents,
            rec: {
              ...rec,
              carrierId: sec.carrier.id, carrierName: sec.carrier.name,
              productId: p.product.id, productName: p.product.name,
              monthlyCents: pr.monthlyCents, annualCents: pr.monthlyCents * 12,
              rateClass: pr.rateClass, faceCents: pr.faceCents,
            },
          });
        }
      }
    }
    others.sort((a, b) => a.monthlyCents - b.monthlyCents);
    if (others.length) {
      body.append(el('div', { class: 'qalts' },
        el('div', { class: 'qlab', text: 'Also available' }),
        ...others.slice(0, 3).map((o) => row({
          title: o.label,
          sub: `$${(o.monthlyCents / 100).toFixed(2)} a month`,
          right: button('Start it', 'sm', () => void save(o.rec, true)),
        }))));
    }

    if (run.unpricedCount > 0) {
      body.append(el('div', {
        class: 'qsub',
        style: 'margin-top:8px',
        text: `${run.unpricedCount} other product${run.unpricedCount === 1 ? '' : 's'} could write them but `
          + 'has no rate card loaded, so it is not in this comparison.',
      }));
    }
  }

  paint();
  if (sex !== null) void price();
}

/* ------------------------------------------------- when they actually answer -- */

/**
 * The hours this agency's leads actually pick up, from its own call history.
 *
 * WHY NOTHING ELSE DOES THIS
 * Every dialer ever sold records dispositions and none of them read them back.
 * The answer to "what hour should we be working" has been sitting in this
 * database since the first call, costs nothing to keep and nothing to compute,
 * and until now nothing looked at it. An agency guessing wrong about it is
 * throwing away a fifth of its conversations for nothing.
 *
 * WHAT IT WILL NOT SAY
 * A rate off a handful of calls. Below the threshold an hour is reported as
 * "not tried enough" and no percentage is printed, because a figure shown at
 * that sample size gets quoted at that sample size — here, by somebody deciding
 * which four hours of the day four people work.
 *
 * WHY THE UNTRIED HOURS ARE AS LOUD AS THE GOOD ONES
 * This is the trap in the whole idea. An agency that has always called between
 * ten and two will discover that its best hours are between ten and two, and a
 * screen that only ranks what was measured sends it back there tomorrow. The
 * measurement confirms the habit and calls it evidence. Naming the gap does not
 * fix that on its own — nothing purely observational can — but it puts it where
 * a person can see it, and costs nothing.
 */
async function reachPanel(host: HTMLElement): Promise<void> {
  clear(host);
  let r: Reach;
  try {
    r = await get<Reach>('/v1/reach');
  } catch {
    // An extra, not the screen. The queue above still works without it.
    return;
  }
  if (r.attempts === 0) return;

  /*
   * Shown above everything, and shown even when there is not enough data for
   * anything else. It is the only number on this panel that answers a question
   * nobody asked, and the only one that could matter in a courtroom.
   */
  const outside = r.outsideWindow
    ? el('div', { class: 'warnbox', style: 'margin-bottom:14px' },
      el('b', null, `${r.outsideWindow.toLocaleString()} recorded call${r.outsideWindow === 1 ? '' : 's'} `
        + 'fell outside the hours that person\'s state permits. '),
      'Worth a look rather than an accusation: a lead whose state was corrected afterwards, a '
      + 'service call to somebody who already bought, or a call that began at 8:58pm and ended '
      + 'after nine all land here. So does a genuine mistake, which is the point.')
    : null;

  if (r.thin) {
    host.append(panel('When your leads actually pick up',
      outside,
      empty(`${r.attempts} call${r.attempts === 1 ? '' : 's'} so far — not enough to say yet`,
        'Once there are a few hundred recorded outcomes this reads them back and says which '
        + 'hours and which area codes answer. Stating it sooner would be guessing, and an '
        + 'agency would rearrange its week around the guess.')));
    return;
  }

  const base = r.baselineRate ?? 0;
  const peak = Math.max(...r.byHour.map((b) => b.rate ?? 0), base, 0.01);

  const hourRow = (b: Bucket): HTMLElement => {
    const h = Number(b.key);
    const isBest = r.bestHour === h;
    const width = b.rate === null ? 0 : Math.round((b.rate / peak) * 100);
    return el('div', { style: 'display:flex;align-items:center;gap:10px;margin:3px 0' },
      el('span', {
        style: 'width:52px;text-align:right;font-size:12.5px;color:var(--ink-3)',
        text: clockHour(h),
      }),
      el('div', { style: 'flex:1;height:16px;background:var(--ink-6, rgba(0,0,0,.06));border-radius:3px;overflow:hidden' },
        el('div', {
          style: `height:100%;width:${width}%;background:var(--${isBest ? 'good' : 'iris'})`,
        })),
      el('span', {
        style: `width:120px;font-size:12px;color:var(--ink-${b.rate === null ? '4' : '3'})`,
        text: b.rate === null
          ? `${b.attempts} call${b.attempts === 1 ? '' : 's'} — not tried enough`
          : `${pct(b.rate)} of ${b.attempts}`,
      }));
  };

  const headline = r.bestHour === null
    ? null
    : el('div', { class: 'okbox', style: 'margin-bottom:14px' },
      el('b', null, `${clockHour(r.bestHour)} is your best hour. `),
      r.upliftContacts !== null
        ? `The same ${r.attempts.toLocaleString()} calls made at that hour would have been about `
          + `${r.upliftContacts.toLocaleString()} more conversations. That is an upper bound rather `
          + 'than a promise — an hour is partly good because of who gets called then — but the gap '
          + 'is real and it is free to close.'
        : 'Measured across your own calls, in each lead\'s own time zone.');

  const untried = r.untriedHours.length
    ? el('div', { class: 'note', style: 'margin-top:14px' },
      el('b', null, `Never properly tried: ${r.untriedHours.map(clockHour).join(', ')}. `),
      'These are lawful hours in the states you call, and you have not made enough calls in them '
      + 'to know whether they are any good. The hours above look best partly because they are the '
      + 'only ones with a real answer — a week of trying one of these is how that stops being true.')
    : null;

  const bestDay = r.byDay
    .filter((b) => b.rate !== null)
    .reduce<Bucket | null>((a, b) => (a === null || (b.rate ?? 0) > (a.rate ?? 0) ? b : a), null);

  host.append(panel('When your leads actually pick up',
    outside,
    headline,
    el('p', {
      style: 'font-size:12.5px;color:var(--ink-3);margin:0 0 12px',
      text: `${r.contacts.toLocaleString()} conversations out of ${r.attempts.toLocaleString()} calls `
        + `over ${r.windowDays} days — ${pct(base)} overall. Every hour below is the hour where the `
        + 'person you rang lives, not the hour on your own clock.',
    }),
    el('div', null, ...r.byHour.map(hourRow)),
    untried,
    bestDay
      ? el('p', {
        style: 'font-size:12.5px;color:var(--ink-3);margin:14px 0 0',
        text: `Best day of the week: ${DAY_NAME[Number(bestDay.key)] ?? '—'}, at ${pct(bestDay.rate ?? 0)} `
          + `of ${bestDay.attempts.toLocaleString()} calls.`,
      })
      : null,
    r.byAreaCode.some((b) => b.rate !== null)
      ? el('div', { style: 'margin-top:16px' },
        el('div', { style: 'font-size:12.5px;color:var(--ink-3);margin-bottom:8px' },
          'By area code. Bear in mind a bad area code is often a bad list rather than a bad '
          + 'region — check where those leads came from before you write the numbers off.'),
        el('div', { class: 'rows' }, ...r.byAreaCode
          .filter((b) => b.rate !== null)
          .sort((a, b) => (b.rate ?? 0) - (a.rate ?? 0))
          .slice(0, 12)
          .map((b) => row({
            title: `${b.key}${b.state ? ` · ${b.state}` : ''}`,
            sub: `${b.contacts.toLocaleString()} of ${b.attempts.toLocaleString()} answered`,
            right: el('span', {
              class: 'mono',
              style: `color:var(--${(b.rate ?? 0) >= base ? 'good' : 'ink-3'})`,
              text: pct(b.rate ?? 0),
            }),
          }))))
      : null,
    r.capped
      ? el('p', {
        style: 'font-size:12px;color:var(--ink-4);margin:12px 0 0',
        text: 'Read from the most recent calls in the window; there are more behind them.',
      })
      : null));
}

/* ------------------------------------------------------ reasons to call -- */

/** One entry from `GET /v1/call-reasons`. */
interface CallReasonRow {
  kind: string;
  leadId: string;
  policyId: string;
  weight: number;
  headline: string;
  detail: string;
  /** TRUE for a call about a policy this person already owns. */
  servicing: boolean;
  daysUntil: number | null;
}

/**
 * People in the book who are worth a call today, and why.
 *
 * WHY THIS IS THE MOST VALUABLE LIST ON THE SCREEN
 * Everything below it is a cold queue: names, in a lawful order. This is the
 * opposite — a short list of people who already said yes, each with a specific,
 * dated, money reason to ring them. A draft that is about to come out before
 * the client has been paid is a policy that dies and a commission clawed back.
 * A case stuck on a paramed exam is commission nobody is being paid. A placed
 * policy with no commission line is money never collected.
 *
 * It was built, ranked, tested — and no screen called it. An agent could not
 * see the one list in this system most likely to change their week.
 *
 * WHY SERVICING IS MARKED, LOUDLY
 * Almost everything here is a call to an existing client about a policy they
 * own. That is a different call, legally and in what it should sound like,
 * from calling a stranger about buying one — and treating the in-force book as
 * a dialling list is precisely the mistake this system is built not to make.
 * So the seam is drawn on the screen rather than left to be remembered.
 */
async function reasonsPanel(host: HTMLElement, go: (p: string) => void): Promise<void> {
  clear(host);
  let items: CallReasonRow[] = [];
  try {
    items = await get<CallReasonRow[]>('/v1/call-reasons?limit=12');
  } catch {
    // An extra, not the screen. The dialling queue still loads without it.
    return;
  }
  if (items.length === 0) return;

  const rows = el('div', { class: 'rows' });
  for (const r of items) {
    const open = button('Open', 'sm');
    open.addEventListener('click', () => go(`/leads/${r.leadId}`));

    const marks: HTMLElement[] = [];
    if (r.servicing) marks.push(chip('about a policy they own', 'cop'));
    if (r.daysUntil !== null) {
      marks.push(chip(
        r.daysUntil <= 0 ? 'today' : r.daysUntil === 1 ? 'tomorrow' : `in ${r.daysUntil} days`,
        r.daysUntil <= 2 ? 'crit' : '',
      ));
    }

    rows.append(row({
      title: r.headline,
      sub: r.detail,
      right: el('div', { style: 'display:flex;gap:8px;align-items:center;justify-content:flex-end;flex-wrap:wrap' },
        ...marks, open),
    }));
  }

  host.append(panel(
    items.length === 1 ? 'One person worth a call today' : `${items.length} people worth a call today`,
    el('div', { class: 'note', style: 'margin-bottom:12px' },
      el('b', null, 'From your own book, not from a list. '),
      'Each of these is a specific piece of money at a specific date — a draft about to come '
      + 'out too early, a case stuck on a requirement, or a commission that never arrived. '
      + 'A call about a policy somebody already owns is a service call, not a sales call, and '
      + 'should sound like one.'),
    rows,
  ));
}
